import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Money } from '@/components/ui/Money';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import { ViewToggle as SharedViewToggle } from '@/components/ui/ViewToggle';
// The one calendar check in the app: shape AND existence, mirroring the API's
// own 422 on '2026-02-30'. Never reimplemented here.
import { isRealDate } from '@/components/catalog/form-utils';
import { PencilIcon, PlusIcon, SearchIcon, TrashIcon } from '@/components/catalog/icons';
import { DealFormModal } from '@/components/deals/DealFormModal';
import { DealIcon } from '@/components/deals/DealBits';
import { DealLostReasonDialog } from '@/components/deals/DealLostReasonDialog';
import { DEAL_CREATE_TARGETS, CreateFromDealMenu } from '@/components/records/RecordCreateMenu';
/*
 * Quote PDF preview (owner 2026-10-25): ONE viewer host for the page, opened by
 * the per-deal picker on the table row, the phone card and the board card. The
 * picker lists the deal's quotes itself; a row only says WHICH deal.
 */
import { DealQuotesPdfMenu } from '@/components/quotes/DealQuotesPdfMenu';
import { useQuotePdfPreview } from '@/components/quotes/QuotePdfPreview';
import { canPreviewQuotePdf } from '@/lib/quote-pdf-preview';
/*
 * ⚠️ **This list still CONVERTS nothing, and the row "+" menu does not change
 * that.** Conversion is a server-side operation on `DealDetailPage`
 * (`POST /deals/{id}/convert`, `POST /deals/{id}/invoices`) and it carries the
 * deal's line items across; nothing a create form can be seeded with does. So
 * the menu below NAVIGATES TO THE DEAL — `/deals/{id}?create=quote|invoice` —
 * and that page opens the action it already owns. It fires no conversion
 * request, and it must not be turned into one.
 *
 * ⚠️ **It navigates to the DEAL rather than mounting the conversion surfaces
 * here, and that is a decision — do not "improve" it into a modal on this
 * page.** BOTH routes now open a prefilled form on that page:
 * `QuoteFormModal convertFrom={deal}` and, since 2026-08-30,
 * `InvoiceFormModal convertFromDeal={deal}`. Each needs the WHOLE `Deal`
 * object, because each builds the diff BASELINE from it
 * (`convertBaselineFromDeal`, `invoiceBaselineFromDeal`) rather than merely a
 * prefill — and that baseline decides what the follow-up PATCH sends. A list row
 * does not carry one: the rows here are a paginated projection. Mounting a copy
 * would mean fetching the deal anyway AND a second place that must be kept in
 * step with two baseline builders. The conversion path is exactly where a silent
 * divergence produces a wrong customer document.
 *
 * ⚠️ **CORRECTED 2026-08-30 — the invoice branch used to land on a
 * `ConfirmDialog`, and this file said so in two places.** It is a form now, so
 * the two menu items behave alike again; each also fetches the deal's own LINE
 * ITEMS, which is a second reason a list row could not stand in for the record.
 */
/*
 * ⚠️ `ActivityIcon`, NOT `ActivityButton`. The button fetches its own counts, so
 * a page of 25 rows would be 25 extra requests for badges nobody asked for. The
 * icon opens the same sheet under the same mark.
 */
import { ActivityDrawer, ActivityIcon } from '@/components/tasks/ActivityDrawer';
import { DealStageBadge } from '@/components/StageSwatch';
import { KanbanBoard } from '@/components/kanban/KanbanBoard';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { preferencesStore } from '@/lib/preferences-store';
import { useSavedFilterApply, useSavedFilters } from '@/lib/use-saved-filters';
import { effectiveProbability, percentOfDecimal } from '@/lib/decimal';
import { dealValue } from '@/lib/deals';
/*
 * Quote signed (owner 2026-10-27): the label, the filter's two options and the
 * cell wording live in one module shared with deal detail, the UP dashboard and
 * the sales tracker. Server-derived from quote stage FLAGS; never re-derived here.
 */
import {
  QUOTE_SIGNED_FILTER_OPTIONS,
  QUOTE_SIGNED_LABEL,
  quoteSignedLabel,
  type QuoteSignedFilter,
} from '@/lib/deal-quote-signed';
import { formatDateOnly, formatDecimalString, joinList } from '@/lib/format';
import { denialReason } from '@/lib/permissions';
import { viewerZone } from '@/lib/task-time';
import type {
  Deal,
  DealListParams,
  DealListSort,
  KanbanCard,
  KanbanColumn,
  KanbanQueryParams,
  Paginated,
} from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import { useDealPickers } from '@/lib/use-deal-pickers';
/*
 * The currency list lives in `use-invoice-options` because invoices needed it
 * first; it is not invoice-specific. Imported rather than re-derived so this
 * filter offers the same set, order and value — the CHAR(3) code, uppercase.
 */
import { currencyOptions, useCurrencyOptions } from '@/lib/use-invoice-options';
import { useTableFilters, type ColumnSpec } from '@/lib/use-table-filters';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { fetchAllPages, useShowAll } from '@/lib/use-show-all';
/*
 * Column order, hidden set and the two frozen edges — stored against the
 * ACCOUNT under `preferences.columns.deals`, the same scope string the saved
 * filters on this page already use. Read that module's header before changing
 * any of this: the hidden ids are stored rather than the visible ones, and a
 * failed read degrades to the default layout rather than blocking the table.
 */
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
/*
 * The staff directory, read here for TWO things — and they pull in opposite
 * directions, which is why `withGroups` is on while the option list is not
 * narrowed:
 *
 *   - the Account lead FILTER's options, which are the WHOLE directory. It is
 *     not a picker — nothing on this page assigns a lead — and it filters by
 *     whoever a deal ALREADY belongs to, where the group governs who may be
 *     NAMED as one. Narrowing to members would hide every deal led by somebody
 *     since removed from the group, through the only control that reaches them.
 *   - the evidence for that filter's DEFAULT, which is `byGroup.account_lead`,
 *     because "should this person's own deals be the first thing they see" is
 *     genuinely the membership question.
 */
import { useUserOptions } from '@/lib/use-user-options';
/*
 * The Account lead filter's DEFAULT — the whole of it, including the membership
 * test, the three arrival states and the URL write.
 *
 * ⚠️ *SUPERSEDED: this page held the only copy, ~95 lines inline, plus a
 * `USER_GROUP_SLUGS` import it needed for nothing else.* `/quotes` and
 * `/invoices` wanted the same default on 2026-09-12, so it moved to
 * `src/lib/use-default-account-lead.ts` **unchanged** — same states, same order,
 * same refusals. Read that module's header before touching any of this; the
 * reasoning that used to sit here now lives there, beside the code it governs.
 */
import { useDefaultAccountLeadFilter } from '@/lib/use-default-account-lead';
import {
  Avatar,
  Badge,
  Button,
  ColumnManager,
  Combobox,
  ConfirmDialog,
  EmptyState,
  Field,
  FilterableTH,
  FilterSheet,
  Input,
  ListPager,
  MultiCombobox,
  PageHeader,
  Panel,
  PanelHeader,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  THead,
  TR,
} from '@/components/ui';
import type { ComboboxOption, FilterableTHProps, MultiComboboxOption } from '@/components/ui';

const PER_PAGE = 20;
const SKELETON_ROWS = 6;

/*
 * ⚠️ **The row "+" menu, its targets and its URL convention ALL live in
 * `@/components/records/RecordCreateMenu` now.** They were extracted verbatim —
 * portal, `z-[60]` layer rung, keyboard semantics, flip handling and the
 * `/deals/{id}?create=…` convention — so that this list and the Deals card on
 * `/university-partnerships` cannot offer different records or word the control
 * differently. `CREATE_FROM_DEAL_TARGETS` moved with it as
 * `DEAL_CREATE_TARGETS`; the per-module `ability.canCreate` filter stays here,
 * because what is OFFERED is this page's decision.
 */

/*
 * Deliberately the same number as `PER_PAGE`, so "the first 20" means the same
 * thing in both views. The endpoint's default is 20, its ceiling 100, and there
 * is no per-column "load more" route at all — a column that holds more says so
 * and links out to this table filtered to that stage.
 */
const BOARD_CARDS_PER_COLUMN = 20;

/*
 * The id filters live in the URL rather than in state: the backend's 409 on a
 * picker delete points at `/deals?stageId=N`, and a filtered pipeline is worth
 * sharing. `search` stays local — debounced per keystroke, and a half-typed
 * term is not worth a history entry.
 */

/**
 * Reads a comma-separated list of positive integers out of the query string
 * (owner 2026-10-28: every id filter takes several values, same param name).
 * A bare `?stageId=4` from an old bookmark is a list of one. Junk members are
 * dropped, duplicates collapse, and the order ticked is kept.
 */
function parseIds(raw: string | null): number[] {
  if (!raw) return [];
  const out: number[] = [];
  for (const part of raw.split(',')) {
    const text = part.trim();
    if (!/^\d+$/.test(text)) continue;
    const value = Number(text);
    if (Number.isSafeInteger(value) && value > 0 && !out.includes(value)) out.push(value);
  }
  return out;
}

const ID_FILTERS = [
  /*
   * ⚠️ `ownerId` is deliberately NOT here — and the reason is about this PAGE,
   * not about the API. `/deals` and the deals board both accept `ownerId`
   * (`DealListParams.ownerId` is real), but this page renders no Owner column
   * and no owner control, and nothing in this app writes `deal.owner` any more,
   * so honouring `?ownerId=N` from an old bookmark would quietly narrow a list
   * with nothing on screen saying so — most likely to nothing at all. Read as
   * absent instead. Prefer `accountLeadId` for anything meaning "attached to
   * me".
   */
  'stageId',
  'dealTypeId',
  'leadSourceId',
  'companyId',
  'contactId',
  /*
   * 🔴 **MOVED HERE FROM THE COLUMN FUNNEL — 2026-09-11.** *SUPERSEDED:
   * "`accountLeadId` is NOT here either … the Account lead column funnel owns
   * the param through `useTableFilters`. Listing it here too would give one
   * query key two writers with no shared state. Not deep-linkable is the price
   * of having one writer."*
   *
   * ⚠️ **The one-writer rule is UNTOUCHED and is exactly why the funnel had to
   * be demoted in the same change.** The Account lead column is `scope: 'page'`
   * again — see `dealColumns` — so this bank is still the only writer of the
   * key. Two controls for one param remains forbidden; what changed is WHICH
   * control owns it, not how many.
   *
   * Three things the funnel could not do, and each is a requirement now:
   *   - **it is on a TABLE HEADER**, and the board is the default view, so the
   *     one filter meaning "deals attached to me" was unreachable where most
   *     people now land;
   *   - **it was not in the URL**, so it could not be deep-linked, could not be
   *     saved against the account, and could not carry a default that stays
   *     clearable (see `leadFilterSettled`);
   *   - a default has to be *visible*, and a header funnel is not.
   *
   * ⚠️ *SUPERSEDED 2026-10-28 (owner: every filter takes several values):
   * "SINGLE-VALUED, on both surfaces … CSV is parsed by `/companies` and its
   * board ALONE."* `GET /deals` and the deals board take a CSV of ids under the
   * same key now, so `parseIds` admits a list here as on every id filter.
   *
   * Verified in the backend source 2026-09-11 (`kanban.registry.ts`, the deals
   * board's `accountLeadId` is `kind: 'id'` with an `overrideColumn`;
   * `deals.schema.ts` `listDealsQuerySchema` takes the same single id). Both
   * match the RESOLVED lead — the deal's own override, else its company's — so
   * the two views and the column beside them cannot disagree about who a deal
   * belongs to.
   */
  'accountLeadId',
  /*
   * The saleable run a deal belongs to. A real filter on `GET /deals`, arrived
   * at by link from `/projects/:id` rather than from a control here — like
   * `companyId` and `contactId`, which is why it gets a badge, not a select.
   *
   * ⚠️ IT IS NOT A BOARD KEY. The kanban query schema is `.strict()` and built
   * PER BOARD, and NO board declares `projectId` (checked in the backend
   * source). Sending it to `/deals/kanban` is a 422 naming the key, taking a
   * working board down — so it is absent from `boardParams`, excluded from
   * `boardFilterCount`, and the board says it is not narrowed by it. Wire it in
   * the day the registry declares it, and not before.
   */
  'projectId',
] as const;
type IdFilter = (typeof ID_FILTERS)[number];

/** The ids this page sends to the BOARD — every id filter except the one it has. */
const BOARD_ID_FILTERS = ID_FILTERS.filter((key) => key !== 'projectId');

/*
 * The two closing-date bounds, spelled exactly as the DEALS BOARD spells them.
 * They double as the URL keys, so a filtered board is still one honest link.
 */
const DATE_FILTERS = ['closingDateAfter', 'closingDateBefore'] as const;

/*
 * `?quoteSigned=signed|unsigned` (owner 2026-10-27). LIST ONLY: the deals
 * board's `.strict()` query does not declare it, so it is never put on
 * `boardParams`, and the board says it is not narrowed by it — the same
 * treatment as `projectId`. Anything but the two values reads as absent.
 */
const QUOTE_SIGNED_KEY = 'quoteSigned';

function parseQuoteSigned(raw: string | null): QuoteSignedFilter | null {
  return QUOTE_SIGNED_FILTER_OPTIONS.find((option) => option.value === raw)?.value ?? null;
}

const QUOTE_SIGNED_PICKER_OPTIONS: ComboboxOption[] = QUOTE_SIGNED_FILTER_OPTIONS.map(
  (option) => ({ value: option.value, label: option.label }),
);

/**
 * What this page remembers against the account.
 *
 * Every id filter and both date bounds — all of them are in the URL, all of
 * them have a clear affordance, and none of them is `page`. ⚠️ **`new` is
 * deliberately absent**: it is a command that opens the create dialog, not a
 * description of which rows, and remembering it would greet somebody with a
 * blank New Deal form every time they opened this page.
 */
const SAVED_FILTER_KEYS = [...ID_FILTERS, ...DATE_FILTERS, QUOTE_SIGNED_KEY] as const;
/** Merged per key rather than all-or-nothing — see `arrivalDecision`. */
const SAVED_VIEW_KEYS = ['view'] as const;
type SavedDealsKey = (typeof SAVED_FILTER_KEYS)[number] | (typeof SAVED_VIEW_KEYS)[number];
type DateFilter = (typeof DATE_FILTERS)[number];

/**
 * `currency` is a CHAR(3) code and the list endpoint validates it as exactly
 * `^[A-Z]{3}$` — `?currency=usd` is a 422, not folded to uppercase. The board
 * does upper-case server-side, but keeping the URL uppercase for both is the
 * only way the two views agree about a pasted link. Anything else is treated as
 * absent rather than forwarded.
 */
function parseCurrency(raw: string | null): string | null {
  if (!raw) return null;
  return /^[A-Z]{3}$/.test(raw) ? raw : null;
}

/**
 * A closing-date bound out of the query string: 'YYYY-MM-DD' AND a real day.
 * `2026-02-30` matches the shape and is still a 422, so the calendar check is
 * the point. Kept as a string throughout — `new Date('2026-05-31')` is midnight
 * UTC and renders as the 30th west of Greenwich.
 */
function parseDateParam(raw: string | null): string | null {
  if (!raw) return null;
  return isRealDate(raw) ? raw : null;
}

/*
 * Table or board, in `?view=` so either one is a real link.
 *
 * 🔴 **THE BOARD IS THE DEFAULT — owner's instruction, 2026-09-11.**
 * *SUPERSEDED: "THE TABLE IS THE DEFAULT, because the PER-RECORD ACTIONS LIVE
 * THERE — tasks and notes, Edit and Delete are row actions, and the board can
 * carry only a couple through `cardAction`."*
 *
 * ⚠️ **That reasoning was not wrong and is not retired — the owner was shown
 * what it costs and took the change anyway, so record the cost rather than the
 * reversal alone.** Four row actions have no card equivalent: the
 * tasks-and-notes sheet, the "+" quote/invoice menu, Edit and Delete. NONE of
 * them becomes unreachable, and that is the whole reason this is affordable
 * here where it was not on `/companies`: `hrefFor` makes the entire card a
 * stretched `<Link>` to `/deals/{id}`, and that page carries every one of the
 * four — `ActivityButton`, `?create=quote|invoice`, "Edit deal", Delete. So
 * each is one click further away, not gone. ⚠️ **The `/companies` incident this
 * inverts was a menu with NO second home** — the "+" was reported as deleted
 * because there was nowhere else to reach it. Check that property again before
 * defaulting any other list to its board.
 *
 * An absent `view` key means the BOARD; only `?view=table` asks for the table.
 * Anything else — a typo, `?view=kanban` — is the board too. ⚠️ **`?view=table`
 * has always been honoured and still is**, so every link written while the
 * table was the default still lands on the table; only the bare `/deals`
 * address changed meaning.
 */
type DealsView = 'table' | 'board';

function parseView(raw: string | null): DealsView {
  return raw === 'table' ? 'table' : 'board';
}

/**
 * One reference master, as a filter's option list: every row it carries, retired
 * rows labelled, plus the id the URL holds when the master does not carry it.
 *
 * ⚠️ **THE WHOLE MASTER, RETIRED ROWS INCLUDED — a FILTER is not a PICKER.**
 * Reads never narrow on `isActive`, so a live deal can hold a retired stage,
 * type or lead source, and this bank is the only control that reaches those
 * deals. Filtering to active rows would make them unreachable through the one
 * thing that could find them. `(retired)` is the label, not a reason to hide it.
 * That is why these three are NOT active-only the way the currency list beside
 * them is — see `currencyPickerOptions`, which states the departure from its own
 * side.
 *
 * 🔴 **THE PIN-BACK IS WHAT A NATIVE `<select>` USED TO DO FOR FREE, AND WITHOUT
 * IT THE CONTROL MISREPORTS ITS OWN STATE.** A `<select>` handed a value no
 * `<option>` carries shows it anyway; `Combobox` falls back to its PLACEHOLDER.
 * So `?stageId=99` — a row past the master's first page, or one nothing could
 * read — would leave this reading "All stages" over a list genuinely narrowed to
 * one stage: applied, invisible, and with no option to untick. Same job the
 * currency and account-lead pins already do on this bank, arriving on the three
 * controls that became Comboboxes on 2026-09-13.
 *
 * ⚠️ **Pinned UNCONDITIONALLY, and never gated on the master having loaded.** An
 * empty list means the read FAILED, and seeing and clearing the filter you are
 * looking at is exactly what has to survive a failed read — so the list degrades
 * to "the rows we could read, plus the one in the URL" rather than to a closed
 * set of none. The notice under the bank is what says the read failed; this is
 * only responsible for the value staying visible.
 *
 * `#N` is the honest label: the name is not in hand, and inventing one would be
 * a claim about a row we did not read. It is the same idiom the "Also filtered
 * by" badges use for an id they cannot name. ⚠️ **"(retired)" is deliberately
 * NOT claimed on a pinned row** — absent from the master means retired OR past
 * its first page OR unread, and this cannot tell the three apart.
 *
 * Master order is the curated picker order (`sortOrder`, then `name`) and is
 * used as received; the pin goes FIRST so a row we could not read cannot look
 * like one we did.
 */
function masterFilterOptions(
  rows: readonly { id: number; name: string; isActive: boolean }[],
  selected: readonly number[],
): MultiComboboxOption[] {
  const options: MultiComboboxOption[] = rows.map((row) => ({
    value: String(row.id),
    label: row.isActive ? row.name : `${row.name} (retired)`,
  }));
  const known = new Set(options.map((option) => option.value));
  const pinned = selected
    .map(String)
    .filter((chosen) => !known.has(chosen))
    .map((chosen) => ({ value: chosen, label: `#${chosen}` }));
  return pinned.length === 0 ? options : [...pinned, ...options];
}

/*
 * ⚠️ Never cast a board `KanbanCard` to a `Deal`. It invents a stage, an amount
 * and an `accountLead` the board never sent.
 */

/**
 * Table | Board.
 *
 * Two real buttons with `aria-pressed` rather than a `<select>`: two options,
 * both fit at 320px, and the pressed state is the whole message. It lives in the
 * PAGE header — `PanelHeader`'s actions slot is `shrink-0` and cannot wrap.
 */
const VIEW_OPTIONS: readonly { value: DealsView; label: string }[] = [
  { value: 'table', label: 'Table' },
  { value: 'board', label: 'Board' },
];

function ViewToggle({ view, onChange }: { view: DealsView; onChange: (next: DealsView) => void }) {
  return <SharedViewToggle label="Deal view" value={view} options={VIEW_OPTIONS} onChange={onChange} />;
}

/**
 * Expected revenue: the deal's VALUE at its STAGE's percentage.
 *
 * ⚠️ **The value is `dealValue`, not `deal.amount` — owner's ruling,
 * 2026-09-10.** It is the deal's line-item total whenever it has lines and the
 * typed amount only when it has none, so a priced basket forecasts from the
 * basket. Reading `amount` here would put the forecast out of step with the
 * Amount column beside it on the same row, both figures looking perfectly
 * correct.
 *
 * ⚠️ The deal's own `probability` override is no longer read — owner's decision,
 * 2026-08-31; see `effectiveProbability`. A deal that still holds a stored
 * override forecasts from its stage now, which is the intended change.
 *
 * Deliberately `null` rather than `0` when either half is unknown — an unpriced
 * deal and a deal worth nothing are different facts, and so are "no stage" and
 * "0% chance". `dealValue` preserves that on its own side: `linesTotal: null`
 * is "no lines" and falls through to the amount, where `"0.00"` is a real
 * total and does not. Decimal string maths; the value never sees a float.
 */
function expectedRevenue(deal: Deal): string | null {
  return percentOfDecimal(dealValue(deal), effectiveProbability(deal.stage?.probability));
}

function DealName({ deal }: { deal: Deal }) {
  return (
    <div className="min-w-0">
      <TextLink to={`/deals/${deal.id}`} className="text-sm font-medium [overflow-wrap:anywhere]">
        {deal.dealName}
      </TextLink>
      {deal.company && <p className="mt-0.5 truncate text-xs text-fg-muted">{deal.company.name}</p>}
    </div>
  );
}

/**
 * The account lead, RESOLVED — plus which of the two answers it is.
 *
 * `deal.accountLead` is already the right person: the deal's own override when
 * it has one, otherwise its company's. Rendering only that would show a
 * company's choice as though somebody had picked it for this deal, so the
 * second line says which.
 *
 * `accountLead: null` is a real answer rather than a gap — "None / From the
 * company" reads as the company naming nobody, which is the only way a deal can
 * have no account lead.
 */
function AccountLeadCell({ deal }: { deal: Deal }) {
  const source = deal.accountLeadIsOverride
    ? 'This deal’s own'
    : deal.company
      ? 'From the company'
      : 'No company to follow';
  return (
    <div className="min-w-0">
      {deal.accountLead ? (
        <div className="flex min-w-0 items-center gap-2">
          <Avatar name={deal.accountLead.name} size="sm" />
          <span className="truncate text-sm text-fg-secondary">{deal.accountLead.name}</span>
        </div>
      ) : (
        <span className="text-sm text-fg-subtle">None</span>
      )}
      <p className="m-0 mt-0.5 truncate text-xs text-fg-muted">{source}</p>
    </div>
  );
}

/**
 * Quote signed: a real `<Link>` to the signed quote, `—` when no linked quote
 * is signed (`null`) and when an older API sent no key (`undefined`).
 */
function QuoteSignedCell({ deal }: { deal: Deal }) {
  const signed = deal.quoteSigned;
  if (signed === undefined || signed === null) {
    return <span className="text-xs text-fg-subtle">—</span>;
  }
  return (
    <TextLink to={`/quotes/${signed.quoteId}`} className="text-xs whitespace-nowrap">
      {quoteSignedLabel(signed)}
    </TextLink>
  );
}

/** `null` is "not priced", which is not the same as a deal worth zero. */
function MoneyCell({ value }: { value: string | null }) {
  if (value === null) {
    return (
      <span className="text-xs text-fg-subtle" title="Not priced">
        —
      </span>
    );
  }
  return <Money amount={value} />;
}

/*
 * Column filters, and what they can honestly do here.
 *
 * ⚠️ *SUPERSEDED 2026-09-09, and BOTH halves were false: "`GET /deals` has **no
 * sort parameter at all** — ordering is fixed at `id DESC` and an unknown query
 * key is stripped in silence."* **Measured live against production**:
 * `?sort=dealName&order=asc` answers **200**, and `?sort=bogusKey` answers
 * **422 naming the key** — the schema is `.strict()`, so nothing is stripped
 * anywhere here. `DealListSort` in `types.ts` was already right; this comment
 * was the stale artefact.
 *
 * ⚠️ *SUPERSEDED 2026-10-09, owner's ruling: "it is the reason no column
 * declares a `sortKey` and no sort control renders on this page … it is left
 * undone deliberately rather than smuggled in with a comment correction."*
 * **Every filterable column now carries a `sortKey`**, and the order is asked of
 * the SERVER (`sort` + `order` in `load`) — this list pages, so sorting the
 * loaded rows would order page 1 of N and imply the whole pipeline. Each key is
 * pinned with `satisfies DealListSort`, and `isDealSort` is the runtime half, so
 * a mistyped key is a `tsc` error rather than a `.strict()` 422 that empties the
 * table. Sorting NOTHING still sends nothing, so the server default (`id DESC`)
 * is unchanged. It applies to the TABLE view; the board ignores it.
 *
 * Its id filters are already driven
 * by the URL-backed filter bank above the table; declaring them on the headers
 * too would give one query param two writers with no shared state. So every
 * column the bank owns is `scope: 'page'` — the default — and its popover says so.
 *
 * 🔴 **THERE IS NO LONGER AN EXCEPTION, and Account lead was it — 2026-09-11.**
 * *SUPERSEDED: "**Account lead is the one exception, and only because nothing
 * in the bank writes `accountLeadId`.** With no second writer it can be
 * `scope: 'server'`: both `GET /deals` and the deals board read the key, so the
 * funnel narrows the whole pipeline."* The bank writes `accountLeadId` now, so
 * that clause's own precondition is gone and the column follows the same rule
 * as its five neighbours. **EVERY column filter on this page is page-scoped**,
 * which is what collapsed `columnScopeSentence` back to one sentence.
 *
 * ⚠️ **No capability was lost, it MOVED** — whole-pipeline filtering by lead is
 * the bank's Account lead control, which reaches the board too. **Do not
 * restore `scope: 'server'` here**: that would put a second writer on a query
 * key the URL now owns, and the two have no shared state, so they would
 * disagree in silence.
 *
 * The `closing` column filter and the bank's closing-date RANGE are two
 * different mechanisms and stay that way: the column one is a client-side pass
 * over the loaded rows, the bank's two bounds are query params narrowing what
 * the SERVER returns for both views. Only the column filters' notice says "on
 * this page" — they are the only thing that hides anything the request brought
 * back.
 */
/**
 * Built per render because one column needs data: the account-lead column's
 * VALUE is an id, so it needs a name map off the loaded rows (`format`) to
 * label the values it accumulates.
 */
function dealColumns(leadNames: Map<string, string>): ColumnSpec<Deal>[] {
  return [
  {
    key: 'deal',
    label: 'Deal',
    get: (deal) => deal.dealName,
    sortKey: 'dealName' satisfies DealListSort,
  },
  // Every link is nullable and every join is a LEFT JOIN: an unassigned deal is
  // a normal row, and null reads as EMPTY in the filter.
  {
    key: 'stage',
    label: 'Stage',
    get: (deal) => deal.stage?.name ?? null,
    sortKey: 'stage' satisfies DealListSort,
  },
  /*
   * The deal's VALUE — `dealValue`, so the funnel offers the same figures the
   * cells show. Filtering on the typed `amount` while rendering the lines'
   * total would give a popover whose options match no row on screen.
   *
   * An exact DECIMAL string. It is never put through Number(), and null ("not
   * priced") is not "0.00" — the first is EMPTY, the second is not.
   */
  {
    key: 'amount',
    label: 'Amount',
    get: dealValue,
    format: formatDecimalString,
    numeric: true,
    // The server orders `COALESCE(lines_total, d.amount)` — `dealValue`'s rule.
    sortKey: 'amount' satisfies DealListSort,
  },
  {
    key: 'expected',
    label: 'Expected',
    get: expectedRevenue,
    format: formatDecimalString,
    numeric: true,
    sortKey: 'expectedRevenue' satisfies DealListSort,
  },
  // Plain 'YYYY-MM-DD' — kept as a string, compared as a string, never a Date.
  {
    key: 'closing',
    label: 'Closing',
    get: (deal) => deal.closingDate,
    format: formatDateOnly,
    sortKey: 'closingDate' satisfies DealListSort,
  },
  /*
   * The account lead, narrowing THE LOADED PAGE by person — the options are
   * people, never "overridden / not".
   *
   * The VALUE is the id and `format` maps it back to a name, because the id is
   * what the row carries and two people can share a name. It is the RESOLVED
   * lead (`deal.accountLead`), the same value the cell renders and the same one
   * `?accountLeadId=` matches, so this funnel and the bank's control above can
   * never disagree about who a deal belongs to — they only differ in REACH.
   *
   * ⚠️ **`scope: 'page'` (the default), and NO declared `options` — both were
   * removed on 2026-09-11 when the bank took the query key.** With `options`
   * left in place this column would offer the whole staff directory while
   * filtering only the loaded rows, so picking anybody whose deals were not on
   * this page would empty the table and the popover would say nothing about
   * why. Accumulating from the rows instead means every option it offers
   * matches at least one row in front of you, which is what a page-scoped
   * funnel must promise. **Whole-pipeline filtering is the bank's control**;
   * this one is the "narrow what I can see" pass every other column here does.
   *
   * Emptiness is page-scoped here as everywhere — no endpoint on this API
   * exposes an is-null filter — and the popover says so.
   */
  {
    key: 'accountLead',
    label: 'Account lead',
    get: (deal) => deal.accountLead?.id ?? null,
    format: (value) => leadNames.get(value) ?? `User #${value}`,
    // Ordered by the RESOLVED lead's NAME server-side, not by the id.
    sortKey: 'accountLead' satisfies DealListSort,
  },
  /*
   * Quote signed (owner 2026-10-27). ⛔ The id `quoteSigned` is stored in
   * people's column layouts — never rename it. The funnel narrows the loaded
   * page by the cell's own wording; the bank's Signed / Not signed control is
   * the whole-list filter. The server sorts by `signedAt`, unsigned rows last.
   */
  {
    key: 'quoteSigned',
    label: QUOTE_SIGNED_LABEL,
    get: (deal) => (deal.quoteSigned ? quoteSignedLabel(deal.quoteSigned) : null),
    sortKey: 'quoteSigned' satisfies DealListSort,
  },
  ];
}

/**
 * The runtime half of the `satisfies` pins above: `useTableFilters` hands back
 * `sort.key` as a plain `string`, and `GET /deals` is `.strict()`, so a key that
 * is not one of these is never sent.
 */
const DEAL_SORT_KEYS: ReadonlySet<string> = new Set<DealListSort>([
  'dealName',
  'stage',
  'amount',
  'expectedRevenue',
  'closingDate',
  'accountLead',
  'quoteSigned',
]);

function isDealSort(key: string): key is DealListSort {
  return DEAL_SORT_KEYS.has(key);
}

/**
 * The filterable columns, built ONCE at module load with an empty name map —
 * only the account-lead column's `format` reads that map, and nothing here
 * calls it. Everything below derives from this list rather than retyping it.
 */
const DEAL_COLUMN_SPECS = dealColumns(new Map());

/**
 * The same keys in the same order `DealsTableHead` renders them, derived rather
 * than retyped so the mobile filter sheet cannot drift out of step with it.
 *
 * ⚠️ **FILTERABLE columns only — the actions lane is deliberately absent**, and
 * this is the list the sub-`md` `FilterSheet` is handed. The actions cell holds
 * row buttons, not a value anything could filter on, so offering it there would
 * be a funnel over nothing.
 */
const DEAL_COLUMN_KEYS: readonly string[] = DEAL_COLUMN_SPECS.map((column) => column.key);

/**
 * The actions lane.
 *
 * ⚠️ **It IS a column key here while being absent from `dealColumns`, and both
 * halves are load-bearing.** `useFrozenOffsets` indexes the header row's cells
 * by their position in the visible-key list, so every cell the header renders
 * must have a key in that list — an actions cell missing from it would shift
 * every measured freeze offset by one whole column, silently.
 */
const ACTIONS_KEY = 'actions';

/**
 * Every column this table renders, in its DECLARED order — what a reader who
 * has never opened the manager sees. `useColumnLayout` reorders and hides from
 * this list; it never adds to it, so a column added here reaches everybody
 * rather than defaulting to hidden for whoever has stored a layout.
 */
const COLUMN_KEYS: readonly string[] = [...DEAL_COLUMN_KEYS, ACTIONS_KEY];

/**
 * ⛔ **THE ACTIONS LANE MAY NEVER BE HIDDEN OR MOVED.** All five row controls
 * live in that one cell — open, tasks and notes, the "+" menu, edit, delete —
 * and at `md` and up none of them has a second home. This repo has already had
 * that reported once as a deleted feature, when the companies "+" menu became
 * merely unreachable. It stays LISTED in the manager, disabled, so its absence
 * from the list cannot read the same way.
 */
const LOCKED_KEYS: readonly string[] = [ACTIONS_KEY];

/**
 * Keys this table renders with `pinned` rather than `frozen`.
 *
 * ⚠️ **NOT the same set as `LOCKED_KEYS`, and they coincide here by accident.**
 * Locked is about hiding and reordering; pinned is about which edge a cell
 * already occupies. The day this page locks its identifying `deal` column too,
 * that key must stay freezable — so the two lists are declared apart even while
 * they hold the same one member.
 *
 * It is handed to three places, and each needs it for a different reason:
 * `useColumnLayout` so the freeze buttons cannot write a second, conflicting
 * position rule onto a `sticky right-0` cell; `ColumnManager` so those buttons
 * are absent rather than inert; and `useFrozenOffsets` so a genuinely
 * right-frozen column is offset PAST the actions lane instead of being handed
 * offset zero and drawn underneath it.
 */
const PINNED_KEYS: readonly string[] = [ACTIONS_KEY];

/** The manager shows the same word the header does, derived from the one list. */
const COLUMN_LABELS: Record<string, string> = Object.fromEntries([
  ...DEAL_COLUMN_SPECS.map((column) => [column.key, column.label] as const),
  [ACTIONS_KEY, 'Actions'] as const,
]);

function labelOf(key: string): string {
  return COLUMN_LABELS[key] ?? key;
}

/**
 * Why a locked column's switch is disabled, in the reader's own terms.
 *
 * A bare `disabled` makes a locked column and a broken switch identical, which
 * is why this is one of the four kinds of sentence the no-helper-text rule
 * keeps. It is per KEY rather than one string because the reasons genuinely
 * differ — see `ColumnManager`'s own note; today only the actions lane is
 * locked, and its reason is the five row controls that have no second home.
 */
function lockReasonOf(key: string): string | undefined {
  return key === ACTIONS_KEY
    ? 'Every per-record control lives in this column, and none has another home on the table'
    : undefined;
}

/** Right-aligned columns, from the specs rather than a second hand-kept list. */
const NUMERIC_KEYS = new Set(
  DEAL_COLUMN_SPECS.filter((column) => column.numeric).map((column) => column.key),
);

/** The `w-*` request each header carries. Auto layout, so content may outvote it. */
const COLUMN_CLASSES: Record<string, string | undefined> = {
  stage: 'w-44',
  amount: 'w-32',
  expected: 'w-32',
  closing: 'w-32',
  accountLead: 'w-52',
  quoteSigned: 'w-40',
};

/** The tone each body cell carries. Unset where the `TD` default is right. */
const BODY_CELL_CLASSES: Record<string, string | undefined> = {
  amount: 'text-fg-secondary',
  expected: 'text-fg-muted',
  closing: 'text-xs whitespace-nowrap text-fg-muted tabular',
};

/**
 * What each column contributes to the table's minimum width, in px.
 *
 * ⚠️ *SUPERSEDED: a hardcoded 1176px floor, written out as a bracket class at
 * BOTH `Table`s.* ⚠️ **The old class is spelled with a gap everywhere it is
 * mentioned below — `min-w-[1176 px]` — because TAILWIND SCANS THIS FILE**, and
 * a class quoted intact in a comment is emitted into the production stylesheet.
 * The five figures below are the `w-*` those headers already declare (`w-44` =
 * 176, `w-32` = 128, `w-52` = 208), `actions` is the 96 the old comment named as
 * its allowance, and `deal` takes the remainder — **so with every column shown
 * the sum is still exactly 1176 and nothing moves.** It is one place now because
 * the number has to shrink as columns are hidden.
 */
const COLUMN_MIN_WIDTHS: Record<string, number> = {
  deal: 312,
  stage: 176,
  amount: 128,
  expected: 128,
  closing: 128,
  accountLead: 208,
  // `w-40`. Added 2026-10-27, so the full-width floor is 1336 now, not 1176.
  quoteSigned: 160,
  [ACTIONS_KEY]: 96,
};

function sumMinWidths(visibleKeys: readonly string[]): number {
  return visibleKeys.reduce((total, key) => total + (COLUMN_MIN_WIDTHS[key] ?? 0), 0);
}

/*
 * The Actions column, indexed by how many controls actually render — up to FOUR
 * today (open the deal, tasks and notes, edit, delete), the last two separate
 * abilities. Same lookup as `CompaniesPage`.
 *
 * ⚠️ The unused `w-60` tail is KEPT: the array is indexed by a count, and a
 * shorter array would silently clamp the last control to a narrower width. Six
 * controls since 2026-10-25 (the quote PDF picker joined the lane).
 * Indexes 0 and 1 are unreachable — the first two controls are ungated.
 */
const ACTION_COL_WIDTHS = ['', 'w-20', 'w-28', 'w-36', 'w-44', 'w-52', 'w-60'] as const;

/** What every part of the table needs to place a frozen column. */
interface FreezeProps {
  frozenSideOf: (key: string) => 'left' | 'right' | undefined;
  frozenOffsets: Record<string, number>;
}

/** `actionCount` is how many row buttons the signed-in user actually gets. */
function DealsTableHead({
  actionCount,
  headerFor,
  visibleKeys,
  headRowRef,
  frozenSideOf,
  frozenOffsets,
}: FreezeProps & {
  actionCount: number;
  headerFor: (key: string) => FilterableTHProps;
  visibleKeys: readonly string[];
  headRowRef: RefObject<HTMLTableRowElement | null>;
}) {
  return (
    <THead>
      {/*
        * The ref is what `useFrozenOffsets` measures. A freeze offset is the
        * summed RENDERED widths of the cells outboard of the frozen one, and
        * this table is auto layout — a `w-*` on a header is only a request the
        * content can outvote — so the DOM is the only authority and the row
        * element has to be reachable.
        */}
      <TR ref={headRowRef}>
        {visibleKeys.map((key) =>
          key === ACTIONS_KEY ? (
            /*
             * Pinned to the right edge of the scroller: this table is wider than
             * the content area from 1440 down, and unpinned the whole column
             * scrolled off the right. Pinned in three places — here,
             * `TableSkeleton` and the body cell.
             *
             * ⚠️ **`pinned`, never `frozen`** — `pinned` IS `frozen="right"` at
             * offset zero, and it is what fifteen pages already say. Any OTHER
             * right-frozen column has to be offset PAST it, which is what
             * `PINNED_KEYS` tells `useFrozenOffsets`: the lane is counted as a
             * right edge by the measurement while rendering as `pinned` here.
             */
            <FilterableTH
              key={key}
              label="Actions"
              numeric
              pinned
              className={ACTION_COL_WIDTHS[Math.min(actionCount, ACTION_COL_WIDTHS.length - 1)]}
            />
          ) : (
            <FilterableTH
              key={key}
              {...headerFor(key)}
              className={COLUMN_CLASSES[key]}
              frozen={frozenSideOf(key)}
              frozenOffset={frozenOffsets[key]}
            />
          ),
        )}
      </TR>
    </THead>
  );
}

/** One skeleton cell's contents, in the shape the real cell fills. */
function SkeletonCell({ columnKey }: { columnKey: string }) {
  switch (columnKey) {
    case 'deal':
      return (
        <div className="grid gap-1.5">
          <Skeleton className="h-3 w-48" />
          <Skeleton className="h-2.5 w-28" />
        </div>
      );
    case 'stage':
      return <Skeleton className="h-4 w-24" />;
    case 'amount':
    case 'expected':
      return <Skeleton className="ml-auto h-3 w-16" />;
    case 'accountLead':
      return (
        // Two lines, matching the account lead cell: the person, and whether
        // the deal chose them or is following its company.
        <div className="grid gap-1.5">
          <div className="flex items-center gap-2">
            <Skeleton className="h-6 w-6 rounded-full" />
            <Skeleton className="h-3 w-24" />
          </div>
          <Skeleton className="h-2.5 w-20" />
        </div>
      );
    default:
      return <Skeleton className="h-3 w-20" />;
  }
}

/**
 * One data cell's contents.
 *
 * ⚠️ **The actions lane is NOT here**, and deliberately: its five controls close
 * over `navigate`, the two modals and the delete flow, so it stays inline in the
 * page where those live rather than being threaded through six props.
 */
function DealCell({ columnKey, deal }: { columnKey: string; deal: Deal }) {
  switch (columnKey) {
    case 'deal':
      return <DealName deal={deal} />;
    case 'stage':
      return <DealStageBadge stage={deal.stage} />;
    // `dealValue`, never `deal.amount` — the lines win when there are lines, and
    // this is the same function the Amount funnel reads, so the options a filter
    // offers can never be figures no cell shows.
    case 'amount':
      return <MoneyCell value={dealValue(deal)} />;
    case 'expected':
      return <MoneyCell value={expectedRevenue(deal)} />;
    case 'closing':
      return <>{deal.closingDate ? formatDateOnly(deal.closingDate) : '—'}</>;
    case 'accountLead':
      return <AccountLeadCell deal={deal} />;
    case 'quoteSigned':
      return <QuoteSignedCell deal={deal} />;
    default:
      return null;
  }
}

function TableSkeleton({
  actionCount,
  visibleKeys,
  frozenSideOf,
  frozenOffsets,
}: FreezeProps & { actionCount: number; visibleKeys: readonly string[] }) {
  return (
    <TBody>
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <TR key={i}>
          {visibleKeys.map((key) =>
            key === ACTIONS_KEY ? (
              <TD key={key} numeric pinned>
                <div className="flex justify-end gap-1.5">
                  {Array.from({ length: Math.max(actionCount, 1) }, (_, n) => (
                    <Skeleton key={n} className="h-6 w-6" />
                  ))}
                </div>
              </TD>
            ) : (
              <TD
                key={key}
                numeric={NUMERIC_KEYS.has(key)}
                frozen={frozenSideOf(key)}
                frozenOffset={frozenOffsets[key]}
              >
                <SkeletonCell columnKey={key} />
              </TD>
            ),
          )}
        </TR>
      ))}
    </TBody>
  );
}

/**
 * The table's minimum width, as an INLINE STYLE.
 *
 * ⛔ **Not a class.** It has to shrink as columns are hidden, and ⚠️ **Tailwind
 * v4 scans SOURCE TEXT, so `min-w-[${n}px]` emits no rule at all** — the class
 * lands in the DOM and does nothing, which looks exactly like a table that
 * chose not to have a floor. It goes straight onto `Table`, which takes a
 * `style` for exactly this — ⚠️ *SUPERSEDED: a spacer `<div>` inside the
 * scroller carrying the number, because `Table` took no `style`.* That wrapper
 * existed only to hold this one value and is gone; the floor now sits on the
 * element it describes, as `min-w-[1176 px]` on the table itself used to. (The
 * gap is deliberate — Tailwind scans this file and would emit the class if it
 * were quoted intact.)
 *
 * ⚠️ **The skeleton and the real rows must carry the IDENTICAL number**, or the
 * columns sit in different places and the table jumps on load. They did that by
 * a duplicated literal before; they do it by this one component now.
 *
 * `frozenHeader` rides on this one wrapper for the same reason: skeleton and
 * rows share the scroll area's shape, so nothing jumps when the data lands. The
 * board view never renders this component.
 */
function DealsTable({ minWidth, children }: { minWidth: number; children: ReactNode }) {
  return (
    <TableWrap frozenHeader>
      <Table style={{ minWidth }}>{children}</Table>
    </TableWrap>
  );
}

function ListSkeleton() {
  return (
    <ul className="m-0 list-none p-0">
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <li
          key={i}
          className="flex items-start justify-between gap-3 border-b border-border px-4 py-3 last:border-b-0"
        >
          <div className="grid flex-1 gap-1.5">
            <Skeleton className="h-3 w-44" />
            <Skeleton className="h-2.5 w-28" />
            <Skeleton className="h-2.5 w-36" />
          </div>
          <Skeleton className="h-4 w-16" />
        </li>
      ))}
    </ul>
  );
}

export function DealsPage() {
  /*
   * `user` has TWO readers now. ⚠️ *SUPERSEDED: "for the activity sheet
   * alone".*
   *
   *   - the activity sheet — the viewer's id decides which notes are the
   *     reader's own, and their zone renders due dates in it;
   *   - the Account lead filter's DEFAULT, which starts on the signed-in
   *     person. See `seededLeadId`.
   */
  const { ability, user } = useAuth();

  /*
   * The read gate. A hidden sidebar row is an optimisation and never a
   * guarantee — the URL is typeable, the command palette offers the page, and a
   * permission set can change under an access token that lasts days — so the
   * page answers for itself.
   *
   * ⚠️ DEGRADES OPEN: only an explicit `none` scope denies. See `can()` in
   * `@/lib/permissions`, which carries the reasoning.
   *
   * ⚠️ **Hiding a page is not refusing a request.** `GET /deals` answers the
   * same to everybody, so nothing here may be worded as though the data were
   * protected. This is a view control.
   *
   * ⚠️ Computed here rather than as an early return, because the return has to
   * sit below every hook. The write gates below are untouched.
   */
  const canReadModule = ability.canRead('deals');

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Saved filters, against the EMERGE account rather than this browser. A link
  // naming any filter wins entirely; `view` still merges per key.
  const applySavedFilters = useSavedFilterApply<SavedDealsKey>();
  /*
   * ⚠️ **The return value is READ now** — it was discarded until 2026-09-11.
   * This page has TWO arrival writers since the account-lead default landed,
   * and `savedFilters.seeding` / `.arrival` are how the second one knows the
   * first has finished. See `leadFilterSettled`.
   */
  const savedFilters = useSavedFilters({
    scope: 'deals',
    keys: SAVED_FILTER_KEYS,
    independentKeys: SAVED_VIEW_KEYS,
    store: preferencesStore,
    apply: applySavedFilters,
  });

  const [result, setResult] = useState<Paginated<Deal> | null>(null);
  const [search, setSearch] = useRememberedState('search', '');
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  /*
   * "Show all" on the TABLE view — every deal matching the current filters,
   * read 100 at a time into one list, remembered for the account. While it is
   * on `page` is ignored (and never written anywhere), and `truncated` says the
   * fetch stopped at its cap. The board is untouched by it.
   */
  const [showAll, setShowAll] = useShowAll('deals');
  const [truncated, setTruncated] = useState(false);

  /*
   * `lockCompany` marks a form opened FOR a company — the "+ New deal" route off
   * the companies list — as opposed to the toolbar's own New deal button, which
   * seeds nothing even under a company filter. A flag rather than the company
   * itself, so the fetched name can arrive late without reopening the form.
   */
  const [formState, setFormState] = useState<{ deal?: Deal; lockCompany?: boolean } | null>(null);

  /*
   * Which row's tasks-and-notes sheet is open, held as the deal itself so the
   * sheet keeps its heading while closing rather than flashing blank.
   *
   * ⚠️ Not `ActivityButton` per row — see the import comment. If counts are ever
   * wanted here, `/tasks/summary` takes up to 200 ids in ONE call: one request
   * for the page, never one per row.
   */
  const [activityFor, setActivityFor] = useState<Deal | null>(null);

  /**
   * Bumped on every successful save so the board refetches; see `handleSaved`.
   * The board only compares it with the value it last saw, so the number itself
   * never has to mean anything.
   */
  const [boardRefresh, setBoardRefresh] = useState(0);

  /*
   * A board drag into an `isLost` stage, waiting on the lost-reason dialog.
   * `resolve` answers the board's `confirmMove`: 'handled' once the dialog's
   * own PATCH lands (the board then reloads), 'cancel' when it is dismissed.
   * The flag decides, never the stage name.
   */
  const [lostMove, setLostMove] = useState<{
    deal: { id: number; dealName: string };
    stage: { id: number; name: string };
    resolve: (decision: 'cancel' | 'handled') => void;
  } | null>(null);
  const confirmBoardMove = useCallback(
    (card: KanbanCard, toColumn: KanbanColumn): Promise<'proceed' | 'cancel' | 'handled'> => {
      if (toColumn.isLost !== true || toColumn.id === null) return Promise.resolve('proceed');
      const stage = { id: toColumn.id, name: toColumn.name };
      return new Promise((resolve) => {
        setLostMove({
          deal: { id: card.id, dealName: card.title ?? '' },
          stage,
          resolve,
        });
      });
    },
    [],
  );
  /*
   * The name behind `?companyId=N`, WITH the id it was fetched for. A pair
   * rather than a bare string so a stale name is discarded during render:
   * clearing it inside the effect paints the previous company's name for a
   * frame, under a filter that no longer selects it.
   */
  const [loadedCompany, setLoadedCompany] = useState<{ id: number; name: string } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Deal | null>(null);
  const [pendingLineCount, setPendingLineCount] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const pickers = useDealPickers();
  /*
   * ⚠️ *SUPERSEDED: "No staff picker on this page … nothing here reads the user
   * list."* It does now — **for the Account lead FILTER's options, not for a
   * picker.** Owner is still not offered on deals and nothing here writes a lead.
   *
   * 🔴 **`withGroups` is ON since 2026-09-11, and it buys exactly ONE thing: the
   * evidence for the default.** It costs one extra request (`/user-groups`,
   * unpaginated, cached by nothing), so it is worth saying what it does NOT buy.
   * ⚠️ *SUPERSEDED: "against a limiter of 120 per 60 seconds shared by the whole
   * office."* The limiter is **12,000/60s** (corrected 2026-09-17). Accounting for
   * the request is still right — it is unpaginated, cached by nothing, and buys
   * one fact — but the arithmetic that reason cited is off by a factor of a
   * hundred.
   *
   * ⛔ **IT DOES NOT NARROW THE FILTER'S OPTIONS, AND MUST NOT.** The control
   * below still offers `staff.users` — the WHOLE directory. The
   * `account_lead` group governs who may be NAMED as a lead; this funnel filters
   * by whoever a deal ALREADY belongs to, and those are different populations.
   * Narrowing here would hide every deal led by somebody since removed from the
   * group, through the only control that can reach them.
   *
   * `byGroup` is read at `seededLeadId` alone, where the question really is
   * "may this person be named as an account lead".
   *
   * ⚠️ **Still NO "staff could not be loaded" notice, and that is deliberate
   * rather than an omission.** The bank's failure line says a dropdown "is empty
   * rather than complete". On a failed read the control below degrades to the
   * leads the loaded rows name plus whatever the URL holds — narrower, never
   * empty — and the default simply does not apply. A failure sentence naming a
   * consequence that did not happen is worse than none.
   */
  const staff = useUserOptions(true, true);
  const currencies = useCurrencyOptions();

  /*
   * ── The Account lead filter starts on the SIGNED-IN PERSON ────────────────
   *
   * Owner's instruction, 2026-09-11: a bare `/deals` shows the deals attached
   * to whoever is looking, rather than the whole pipeline. Extended to
   * `/quotes` and `/invoices` on 2026-09-12, which is why the mechanism moved
   * to `useDefaultAccountLeadFilter` — READ ITS HEADER; every warning that used
   * to be written out here is there, next to the code it governs.
   *
   * ⚠️ **`setPage(1)` is this page's half of the bargain and is not optional.**
   * `page` is component state here rather than a URL key, so the hook cannot
   * reset it and says so. A seed IS a filter change: the hook waits on a
   * preferences read that can take seconds, so a reader may already have paged,
   * and page 4 of the whole pipeline is a page number for a different result
   * set — it comes back empty and reads as "no deals".
   *
   * ⚠️ **THE LINT WARNING WENT AWAY AND THE RESET DID NOT — do not read the
   * green result as permission to drop it.** *SUPERSEDED: "oxlint's
   * `react(set-state-in-effect)` fires on this line, as it does on seven other
   * call sites here."* It fired while `setPage(1)` sat directly inside the
   * effect; passed as a callback the rule no longer sees it, so **this page went
   * from one warning to zero without one line of behaviour changing**
   * (measured 2026-09-12). That was never the point of the move and is not a fix
   * — the reset is still doing the same work, and a future reader who deletes it
   * because "nothing complains" removes the guard rather than the noise. It was
   * never a cascading render either: `page` is already 1 on an ordinary arrival
   * and React bails out on an identical value.
   */
  useDefaultAccountLeadFilter({
    user,
    staff,
    savedFilters,
    onSeeded: () => setPage(1),
  });

  /*
   * Keyed on `result`: `?? []` is a fresh array every render, and both the
   * column filters and the name map below derive from this reference. Hoisted
   * above `load` because the account-lead funnel is `scope: 'server'` — the
   * request depends on the filter state, so that state must exist first.
   */
  const rows = useMemo(() => result?.data ?? [], [result]);

  /*
   * id -> name for the account-lead funnel's labels, off the loaded rows — the
   * RESOLVED lead, so an overridden deal contributes the person it actually
   * belongs to. `useTableFilters` keeps a label once it has seen the id, so a
   * name learned on one page survives a filter excluding every row carrying it.
   */
  const leadNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of rows) {
      if (row.accountLead) map.set(String(row.accountLead.id), row.accountLead.name);
    }
    return map;
  }, [rows]);

  const columns = useMemo(() => dealColumns(leadNames), [leadNames]);
  const table = useTableFilters(rows, columns);
  /*
   * The SERVER sort a header chose, or null. State inside the hook, so its
   * identity is stable and it is safe as a `load` dependency — the same shape
   * `/quotes` and `/invoices` use, and like theirs it lives in the hook rather
   * than the URL.
   */
  const sort = table.sort;
  const visibleRows = table.visibleRows;
  const hiddenByColumns = rows.length - visibleRows.length;

  /*
   * ⚠️ **`hiddenByColumns` above is ROWS hidden by column FILTERS; everything
   * below is about the columns themselves.** Two different things one word
   * apart, which is why nothing here reuses that name.
   *
   * The scope string is `'deals'` — the same one the saved filters use — but it
   * lands in `preferences.columns.deals`, a SIBLING of `preferences.tables`, so
   * a filter save cannot wipe it and this cannot be mistaken for a filter.
   */
  const columnLayout = useColumnLayout('deals', COLUMN_KEYS, LOCKED_KEYS, PINNED_KEYS);

  /*
   * ⛔ **The actions lane is forced LAST and always present, whatever the stored
   * layout says.** `useColumnLayout` refuses to hide or move a locked key, but
   * it does not stop ANOTHER key being moved past one — and `pinned` is
   * `sticky right-0`, so an actions cell that is not the last column floats over
   * whatever follows it. The stored document is unvalidated by design, so this
   * is enforced where it is rendered rather than trusted.
   */
  const visibleColumnKeys = useMemo(
    () => [...columnLayout.visibleKeys.filter((key) => key !== ACTIONS_KEY), ACTIONS_KEY],
    [columnLayout.visibleKeys],
  );

  const headRowRef = useRef<HTMLTableRowElement | null>(null);

  /*
   * ⚠️ **The actions lane must be COUNTED as a right edge by the measurement
   * while it renders as `pinned`.** `useFrozenOffsets` sums the rendered widths
   * of the right-frozen cells outboard of each one; the actions cell is a
   * right-frozen cell in every way that sum cares about, so leaving it out would
   * hand a second right-frozen column an offset of **zero** — parking it exactly
   * underneath the row buttons, with nothing failing and nothing to see but two
   * columns drawn on top of each other.
   *
   * ⚠️ *SUPERSEDED: a local `frozenSideOf` wrapper answering `'right'` for the
   * actions key.* The hook takes `pinnedKeys` itself now, so the controller's
   * own `frozenSideOf` goes in unwrapped — which is also what keeps the
   * dependency stable. That function is memoised on the two freeze lists, where
   * the controller object is a fresh literal every render, and
   * `useFrozenOffsets` tears down and reattaches its `ResizeObserver` whenever
   * its identity changes.
   */
  const frozenOffsets = useFrozenOffsets(
    headRowRef,
    visibleColumnKeys,
    columnLayout.frozenSideOf,
    PINNED_KEYS,
  );
  const tableMinWidth = useMemo(() => sumMinWidths(visibleColumnKeys), [visibleColumnKeys]);

  /*
   * ⚠️ **No `serverParams` is read any more, and nothing on this page is
   * `scope: 'server'`.** *SUPERSEDED: a `listFilters` memo lifting
   * `serverParams.accountLeadId` off the funnel, plus `serverFiltered` and
   * `pageScopedColumnCount` to word a notice about the one column that reached
   * past the loaded page.* The bank owns `accountLeadId` now, so **every column
   * filter here narrows the loaded page and only that** — which is why the
   * notice below is one sentence again rather than three branches.
   */

  /*
   * What the two date inputs show while it is not yet something that can be
   * sent. `null` means "no local edit — show whatever the URL holds", keeping
   * the URL the single source of truth for every value that IS applied. A
   * half-typed or impossible day (2026-02-30) lives here instead, so the field
   * can show what was typed beside the reason it is not being used.
   */
  const [dateDraft, setDateDraft] = useState<Record<DateFilter, string | null>>({
    closingDateAfter: null,
    closingDateBefore: null,
  });

  // Each is a list (CSV in the URL); `[]` means the key is absent = all.
  // Memoised on the URL so each list is a stable reference for the memos below.
  const filters = useMemo<Record<IdFilter, number[]>>(
    () => ({
      stageId: parseIds(searchParams.get('stageId')),
      dealTypeId: parseIds(searchParams.get('dealTypeId')),
      leadSourceId: parseIds(searchParams.get('leadSourceId')),
      companyId: parseIds(searchParams.get('companyId')),
      contactId: parseIds(searchParams.get('contactId')),
      projectId: parseIds(searchParams.get('projectId')),
      accountLeadId: parseIds(searchParams.get('accountLeadId')),
    }),
    [searchParams],
  );
  const view = parseView(searchParams.get('view'));
  const showBoard = view === 'board';
  /*
   * `new=1` is an INTENT, not a filter — the "+" menu on the companies list
   * links here to CREATE a deal for that company. Cleared when the form closes,
   * so a refresh or back-navigation cannot reopen a dismissed form.
   */
  const wantsNew = searchParams.get('new') === '1';

  /*
   * The company behind `?companyId=N`, by name — the badge and the locked field
   * in the form both need something a person can read. One request per company
   * filter, never one per row, and none at all with no company filter set. A
   * failure is not an error state: the label falls back to the id.
   */
  // Named (and lockable in the create form) only while exactly ONE company is
  // filtered; several companies are a filter, not a company to create for.
  const companyFilterId = filters.companyId.length === 1 ? (filters.companyId[0] ?? null) : null;

  useEffect(() => {
    if (companyFilterId === null) return;
    const controller = new AbortController();
    api
      .getCompany(companyFilterId, controller.signal)
      .then((envelope) => {
        if (controller.signal.aborted) return;
        setLoadedCompany({ id: companyFilterId, name: envelope.data.name });
      })
      .catch(() => {
        // Not an error state — the label falls back to the id below.
        if (controller.signal.aborted) return;
      });
    return () => controller.abort();
  }, [companyFilterId]);

  /** The name only while it is the CURRENT filter's name. */
  const companyName =
    loadedCompany !== null && loadedCompany.id === companyFilterId ? loadedCompany.name : null;

  /** What a locked company field shows — never blank, even before the name lands. */
  const filterCompany =
    companyFilterId === null
      ? null
      : { id: companyFilterId, name: companyName ?? `Company #${companyFilterId}` };

  /*
   * The three filters BOTH views take on top of the ids — `GET /deals` reads
   * them under exactly the spellings the board uses, so one link means the same
   * thing in either view. Never trusted: a pasted `?currency=usd` or
   * `?closingDateAfter=2026-02-30` is a 422, so a value that would not survive
   * the round trip counts as absent and is simply not sent.
   */
  const currency = parseCurrency(searchParams.get('currency'));
  const closingAfter = parseDateParam(searchParams.get('closingDateAfter'));
  const closingBefore = parseDateParam(searchParams.get('closingDateBefore'));
  const quoteSigned = parseQuoteSigned(searchParams.get(QUOTE_SIGNED_KEY));

  /*
   * The local draft if one is held, else the URL, else empty. String comparison
   * only — no Date is built from either of them anywhere on this page.
   */
  const afterInput = dateDraft.closingDateAfter ?? closingAfter ?? '';
  const beforeInput = dateDraft.closingDateBefore ?? closingBefore ?? '';
  const INVALID_DATE = 'Use a real date, as YYYY-MM-DD.';
  const afterError = afterInput !== '' && !isRealDate(afterInput) ? INVALID_DATE : undefined;
  /*
   * `from > to` can only ever return nothing, and on the sibling endpoints it is
   * a 422 naming the later key. Caught here so it shows on the field rather than
   * as a failed request; while it holds, the "to" bound is withheld from both
   * requests rather than sent to fail.
   */
  const rangeInverted =
    afterInput !== '' &&
    beforeInput !== '' &&
    isRealDate(afterInput) &&
    isRealDate(beforeInput) &&
    afterInput > beforeInput;
  const beforeError =
    beforeInput !== '' && !isRealDate(beforeInput)
      ? INVALID_DATE
      : rangeInverted
        ? 'This is before the “from” date.'
        : undefined;

  /*
   * Exactly the keys the LIST request sends, serialised — the effect dependency
   * below, in place of the whole query string. A string and not the parsed
   * object: `filters` is a fresh object every render and would refetch every
   * render; a string compares by value.
   *
   * ⚠️ EVERY URL-BACKED FILTER THE REQUEST SENDS IS IN HERE — derived from
   * `ID_FILTERS` rather than retyped, so a key added to that list joins this
   * one by construction. Leaving one out means changing it leaves the table
   * showing the previous value's rows until something else moves.
   * ⚠️ *SUPERSEDED: "ALL NINE … The tenth thing the request carries,
   * `accountLeadId`, is not in the URL — it is `listFilters`, a dependency of
   * `load` in its own right."* It IS in the URL now, so it is in `ID_FILTERS`
   * and therefore already in this key; `listFilters` no longer exists. **A
   * written count was what made that sentence go stale — there is none here
   * now.** `view` is not a filter and `page` is its own dependency. Nothing the
   * request does not send belongs here: every key in this string is a refetch,
   * and a refetch nothing on the wire reflects is a request spent for nothing.
   * ⚠️ *SUPERSEDED: "the limiter is 120 requests per 60 seconds per IP, shared by
   * the whole office."* It is **12,000/60s** (corrected 2026-09-17).
   */
  const listFilterKey = [
    ...ID_FILTERS.map((key) => `${key}=${filters[key].join(',')}`),
    `currency=${currency ?? ''}`,
    `closingDateAfter=${closingAfter ?? ''}`,
    `closingDateBefore=${closingBefore ?? ''}`,
    `${QUOTE_SIGNED_KEY}=${quoteSigned ?? ''}`,
  ].join('&');

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      const params: DealListParams = { page, perPage: PER_PAGE };
      // Blank keys are omitted, never sent empty: `?stageId=` is a 422 rather
      // than "no filter". `?search=` is the one harmless exception.
      if (search) params.search = search;
      const current = new URLSearchParams(listFilterKey);
      for (const key of ID_FILTERS) {
        // One CSV per key; an empty list sends nothing (`?stageId=` is a 422).
        const ids = parseIds(current.get(key));
        if (ids.length > 0) params[key] = ids.join(',');
      }
      /*
       * Re-read out of the serialised key rather than closed over, so the effect
       * dependency and the request are the same nine values by construction.
       * Parsing again keeps a hand-edited key off the wire.
       */
      const currencyParam = parseCurrency(current.get('currency'));
      if (currencyParam !== null) params.currency = currencyParam;
      const afterParam = parseDateParam(current.get('closingDateAfter'));
      if (afterParam !== null) params.closingDateAfter = afterParam;
      const beforeParam = parseDateParam(current.get('closingDateBefore'));
      if (beforeParam !== null) params.closingDateBefore = beforeParam;
      const quoteSignedParam = parseQuoteSigned(current.get(QUOTE_SIGNED_KEY));
      if (quoteSignedParam !== null) params.quoteSigned = quoteSignedParam;
      /*
       * ⚠️ **`accountLeadId` needs no line of its own here — it is in
       * `ID_FILTERS`**, so the loop above already parsed and sent it. That is
       * the point of moving it into the bank: one writer, one parse, and it
       * cannot fall out of step with `listFilterKey`.
       */
      /*
       * ⚠️ **Both keys, or neither.** Sorting NOTHING sends nothing, which is what
       * keeps the server's own default (`id DESC`); `isDealSort` stops a key the
       * `.strict()` schema would refuse from ever leaving.
       */
      if (sort && isDealSort(sort.key)) {
        params.sort = sort.key;
        params.order = sort.direction;
      }
      /*
       * Show all: the same params with `page`/`perPage` replaced, paged by
       * `fetchAllPages`, which throws rather than returning a partial list.
       * `fetchAllPages` needs a signal; a save-triggered reload passes none.
       */
      const request: Promise<Paginated<Deal>> = showAll
        ? fetchAllPages<Deal>(
            (pageNo, perPage, pageSignal) =>
              api.listDeals({ ...params, page: pageNo, perPage }, pageSignal),
            signal ?? new AbortController().signal,
          ).then((all) => {
            setTruncated(all.truncated);
            return {
              data: all.rows,
              meta: { page: 1, perPage: all.rows.length, total: all.total, totalPages: 1 },
            };
          })
        : api.listDeals(params, signal).then((data) => {
            setTruncated(false);
            return data;
          });
      return request
        .then((data) => {
          setResult(data);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof Error ? caught.message : 'Failed to load deals');
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [page, search, listFilterKey, showAll, sort],
  );

  /*
   * The two views never fetch at the same time. The limiter is **per IP** and a
   * whole office shares one NAT, so a list request for a table nobody is looking
   * at is taken off a colleague. The board half of the same bargain is
   * `enabled={showBoard}` below.
   * ⚠️ *SUPERSEDED: "120 requests per 60 seconds."* It is **12,000/60s**
   * (corrected 2026-09-17) — the per-IP shape is unchanged and is what the
   * argument rests on; the figure was wrong by a factor of a hundred.
   */
  useEffect(() => {
    if (showBoard) return;
    const controller = new AbortController();
    const timer = setTimeout(() => void load(controller.signal), 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [load, showBoard]);

  /*
   * Create and edit are WRITE-role abilities, not admin ones: `POST /deals` and
   * `PATCH /deals/:id` are `requireRole(...WRITE_ROLES)`, so gating them on the
   * admin tier hides buttons from every basic and advanced user allowed to press
   * them. Delete really is admin-only; `permissions.ts` knows the difference.
   */
  const canCreate = ability.canCreate('deals');
  const canUpdate = ability.canUpdate('deals');
  const canDelete = ability.canDelete('deals');

  /*
   * What the row "+" menu may offer. Gated on the QUOTES and INVOICES modules
   * SEPARATELY — never on `deals`, and never on one shared flag: a set can hold
   * `quotes.create` without `invoices.create`, and the menu must then offer one
   * item rather than two or none. An empty list renders no trigger at all.
   */
  const createTargets = useMemo(
    () => DEAL_CREATE_TARGETS.filter((target) => ability.canCreate(target.module)),
    [ability],
  );

  /*
   * The leading `2` is the two UNGATED controls every reader gets: opening the
   * deal, and the tasks-and-notes sheet — reads are open to every role including
   * `viewer`, and the task and note routers carry no role guard. That is why the
   * column can never be empty, and why `ACTION_COL_WIDTHS[0]` and `[1]` are
   * unreachable.
   */
  const canPreviewQuotes = canPreviewQuotePdf(ability);
  const actionCount =
    2 +
    (canPreviewQuotes ? 1 : 0) +
    (createTargets.length > 0 ? 1 : 0) +
    (canUpdate ? 1 : 0) +
    (canDelete ? 1 : 0);

  /* The one quote PDF viewer on this page; every picker opens it. */
  const quotePreview = useQuotePdfPreview();

  /*
   * The form the page is showing: whatever a control opened, else the one the
   * URL asks for. DERIVED, not pushed into state from an effect — copying the
   * URL's intent into state gives two places that can disagree, and an effect
   * that opens a modal paints the page once without it first.
   *
   * A URL IS NOT AUTHORISATION — anyone can type one — so it opens the form only
   * for someone the server would let POST. Refused, `?companyId=N` still means
   * what it says and the page is the filtered list.
   */
  const activeForm = formState ?? (wantsNew && canCreate ? { lockCompany: true } : null);

  /*
   * The project behind `?projectId=N`, by NAME and for free: every deal
   * serialises `project: {id, name}`, so any matching row already carries it. No
   * second request — and no name at all when the filter came back empty, so the
   * badge falls back to `Project #N`.
   */
  const projectNameOf = (projectId: number): string | null =>
    rows.find((row) => row.project?.id === projectId)?.project?.name ?? null;

  const total = result?.meta.total ?? 0;
  const totalPages = Math.max(result?.meta.totalPages ?? 1, 1);
  const firstRow = total === 0 ? 0 : showAll ? 1 : (page - 1) * PER_PAGE + 1;
  const lastRow = total === 0 ? 0 : firstRow + rows.length - 1;
  const initialLoad = loading && !result;
  /*
   * `anyFilter` drives the empty state's wording, so it names only the filters
   * that actually narrowed THIS list — which is all of them: the ids, the
   * search term, the currency and both closing-date bounds all go on the list
   * request, so any one of them can be why it came back empty.
   */
  const anyFilter =
    search !== '' ||
    // `accountLeadId` is one of these now, so it counts here for free — and it
    // is the one most likely to be why an arrival looks empty, since it can
    // arrive already set by the default below.
    ID_FILTERS.some((key) => filters[key].length > 0) ||
    currency !== null ||
    closingAfter !== null ||
    closingBefore !== null ||
    quoteSigned !== null;

  /*
   * The three reference-master filters, each its whole master plus the id in the
   * URL when the master does not carry it. `masterFilterOptions` holds the rule
   * — one copy for three controls, because the retired labelling and the pin-back
   * are the two things that would drift first if they were written out three
   * times, and what would drift is whether a filter can see its own state.
   */
  const stagePickerOptions = useMemo<MultiComboboxOption[]>(
    () => masterFilterOptions(pickers.stages, filters.stageId),
    [pickers.stages, filters.stageId],
  );
  const dealTypePickerOptions = useMemo<MultiComboboxOption[]>(
    () => masterFilterOptions(pickers.dealTypes, filters.dealTypeId),
    [pickers.dealTypes, filters.dealTypeId],
  );
  const leadSourcePickerOptions = useMemo<MultiComboboxOption[]>(
    () => masterFilterOptions(pickers.leadSources, filters.leadSourceId),
    [pickers.leadSources, filters.leadSourceId],
  );

  /*
   * The currency filter's options: the ACTIVE master, plus whatever the URL
   * names that the master does not carry.
   *
   * The value is the three-letter CODE, not an id — a deal stores `currency` as
   * CHAR(3) with no foreign key to the currency table.
   *
   * ⚠️ **ACTIVE-ONLY IS A DELIBERATE DEPARTURE from the rule the other three
   * dropdowns in this bank follow.** Stage, type and lead source list their
   * whole master, retired rows included and labelled, because a FILTER is not a
   * PICKER: reads never narrow on `isActive`, so a live deal can hold a retired
   * stage and hiding it would make those deals unreachable through the only
   * control that reaches them. Currency cannot be treated the same way — the
   * master is 164 rows of which 3 are active (measured 2026-08-31), and 161
   * options nothing can match is not a usable list.
   *
   * ⚠️ **The pin-back below is what pays for that departure, and without it the
   * control MISREPORTS ITS OWN STATE.** `Combobox` renders its PLACEHOLDER when
   * no option matches the value, so `?currency=INR` on a since-retired code
   * would leave this reading "All currencies" over a list genuinely narrowed to
   * one — the filter applied, invisible, and with no option to untick. Same job
   * as `withSelectedValues` does for the companies funnels, for a single-select.
   *
   * ⚠️ **Pinned back UNCONDITIONALLY**, unlike `currencyOptions`' own `current`
   * argument, which pins nothing while the list is empty so a still-loading
   * picker cannot label a perfectly good code "No longer offered". That guard is
   * right for a create picker and wrong here: an empty list means the master
   * FAILED, and seeing and clearing the filter you are looking at is exactly
   * what must survive a failed read. So the list degrades to "the codes we could
   * read, plus the one in the URL" rather than to a closed set of none.
   */
  const currencyPickerOptions = useMemo<ComboboxOption[]>(() => {
    const base = currencyOptions(currencies.currencies, null);
    if (currency === null || base.some((option) => option.value === currency)) return base;
    // The code is its own label: this list does not know the currency's NAME,
    // and the code is the honest answer rather than an invented one. It is a
    // well-formed `^[A-Z]{3}$` by construction — `parseCurrency` treats anything
    // else as absent — so this can never pin a scrap of URL into the list.
    // "No longer offered" is only claimed once a list actually loaded: with
    // none, we know nothing about WHY the code is missing, and the notice under
    // the bank is what says the read failed.
    const pinned: ComboboxOption =
      base.length > 0
        ? { value: currency, label: currency, hint: 'No longer offered' }
        : { value: currency, label: currency };
    return [pinned, ...base];
  }, [currencies.currencies, currency]);

  /*
   * The Account lead filter's option list: the staff directory, plus anybody
   * the loaded rows name that the directory did not, plus whatever the URL
   * holds.
   *
   * ⛔ **THE WHOLE DIRECTORY, NEVER `byGroup.account_lead`.** The group governs
   * who may be NAMED as a lead; this control filters by whoever a deal ALREADY
   * belongs to. Narrowing it would hide every deal led by somebody since
   * removed from the group, through the only control that can reach them —
   * and the list would silently shrink the day an admin edits a membership.
   * The group is consulted for the DEFAULT and nowhere else.
   *
   * ⚠️ **The merge is not belt-and-braces.** `/users` is read as ONE page at
   * the API's ceiling of 100, so a directory larger than that — or a lead whose
   * account has since been removed — would otherwise be unreachable. Every id
   * added from `leadNames` came off a row this endpoint returned, so it is a
   * value `?accountLeadId=` demonstrably accepts.
   *
   * ⚠️ **The URL's own id is pinned back UNCONDITIONALLY, and without it the
   * control MISREPORTS ITS OWN STATE.** `Combobox` renders its PLACEHOLDER when
   * no option matches the value, so `?accountLeadId=42` naming somebody past
   * the first page of `/users`, or somebody whose account has gone, would leave
   * this reading "All account leads" over a list genuinely narrowed to one
   * person — applied, invisible, and with no way to untick it. Same job the
   * currency pin above does, and the same reason it is not gated on the
   * directory having loaded: seeing and clearing the filter you are looking at
   * is exactly what has to survive a failed read.
   *
   * Sorted by name. That is not the "order is preserved" rule being broken:
   * that rule protects a CLOSED VOCABULARY whose sequence means something — a
   * pipeline, a completeness scale. A staff list has no such sequence and
   * `/users` returns it in no order a reader could use, so alphabetical is what
   * somebody hunting a name needs. The pin goes FIRST rather than into the sort,
   * so a person who is not in the directory cannot look like one who is.
   */
  const accountLeadPickerOptions = useMemo<MultiComboboxOption[]>(() => {
    const options: MultiComboboxOption[] = staff.users.map((row) => ({
      value: String(row.id),
      // `name` is NOT NULL on `d_users`, but a blank one renders as a missing
      // record rather than a bad one; the email is the honest fallback and the
      // id is the last resort, never an invented name.
      label: row.name.trim() || row.email.trim() || `User #${row.id}`,
    }));
    const known = new Set(options.map((option) => option.value));
    for (const [value, label] of leadNames) {
      if (known.has(value)) continue;
      known.add(value);
      options.push({ value, label });
    }
    options.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }),
    );
    /*
     * Named from a loaded row where one knows them, and by id where none does.
     * `User #42` is the honest answer rather than an invented name — the same
     * choice `format` makes on the column beside it.
     */
    const pinned = filters.accountLeadId
      .map(String)
      .filter((value) => !known.has(value))
      .map((value) => ({ value, label: leadNames.get(value) ?? `User #${value}` }));
    return pinned.length === 0 ? options : [...pinned, ...options];
  }, [staff.users, leadNames, filters.accountLeadId]);

  /*
   * What could not be read, and what was read but cut short.
   *
   * Kept as lists of names rather than one sentence: the lists behind this bank
   * are independent requests and `Promise.allSettled` lets one fail alone.
   * ⚠️ Rendered as two separate sentences below, for the same reason
   * `formError ?? count` is a bug here — a failure and a truncation can be true
   * at once about different masters, so neither may stand in for the other.
   */
  const failedPickers = [
    pickers.failedBy.stages && 'stages',
    pickers.failedBy.dealTypes && 'types',
    pickers.failedBy.leadSources && 'lead sources',
    currencies.failed && 'currencies',
  ].filter((label): label is string => typeof label === 'string');

  /*
   * ⚠️ **`staff.failed` is deliberately NOT in `failedPickers`, and `hasMore`
   * IS in the list below.** The shared failure sentence says a dropdown "is
   * empty rather than complete", and that would be FALSE of Account lead: on a
   * failed `/users` read the list degrades to the leads the loaded rows name
   * plus whatever the URL holds — narrower, never empty — so it gets its own
   * sentence saying what actually happened. The truncation sentence needs no
   * such care: "showing only the first page" is exactly true of a directory cut
   * off at the API's ceiling of 100.
   */
  const truncatedPickers = [
    pickers.truncatedBy.stages && 'stages',
    pickers.truncatedBy.dealTypes && 'types',
    pickers.truncatedBy.leadSources && 'lead sources',
    staff.hasMore && 'people',
  ].filter((label): label is string => typeof label === 'string');

  /*
   * Every filter this page holds, mapped onto the board's own query so the two
   * views agree about what is being looked at. The board's filters narrow the
   * COLUMN COUNTS AND TOTALS, not just the cards, so a header can never describe
   * a different set from what is under it.
   *
   * ⚠️ Built key by key and NEVER by spreading `filters`: the board schema is
   * `.strict()` and PER BOARD, so an unknown key — or one belonging to another
   * board, like `statusId` or `quoteId` — is a hard 422, not something the
   * server drops. Measure a key against THIS board before adding it; never
   * infer it from the list endpoint or from another board.
   *
   * `search` carries across honestly — both the board and `GET /deals` match
   * `deal_name`. No memo: `useKanban` serialises this object into a stable key
   * precisely because every caller builds it fresh in render.
   */
  const boardParams: KanbanQueryParams = { cardsPerColumn: BOARD_CARDS_PER_COLUMN };
  if (search) boardParams.search = search;
  // Each id filter as one CSV under its own key; an empty list is not sent.
  if (filters.stageId.length > 0) boardParams.stageId = filters.stageId.join(',');
  if (filters.dealTypeId.length > 0) boardParams.dealTypeId = filters.dealTypeId.join(',');
  if (filters.leadSourceId.length > 0) boardParams.leadSourceId = filters.leadSourceId.join(',');
  if (filters.companyId.length > 0) boardParams.companyId = filters.companyId.join(',');
  if (filters.contactId.length > 0) boardParams.contactId = filters.contactId.join(',');
  // Already `^[A-Z]{3}$` by the time it gets here. The list endpoint refuses
  // lowercase, so an uppercase URL is what lets one link mean the same thing in
  // both views.
  if (currency !== null) boardParams.currency = currency;
  // Real calendar dates by construction — an impossible day never reaches here.
  if (closingAfter !== null) boardParams.closingDateAfter = closingAfter;
  if (closingBefore !== null) boardParams.closingDateBefore = closingBefore;
  /*
   * The account-lead filter crosses to the board — which since 2026-09-11 is
   * the view most people arrive on, so this is no longer "a filter set on the
   * table survives the switch": it is the only surface the default is ever seen
   * on by most readers.
   *
   * Measured on the deals board (`totalRecords` 80 -> 1 for a single id), not
   * assumed from the companies one, and re-verified in the backend source
   * 2026-09-11: the registry declares it `kind: 'id'` with an `overrideColumn`,
   * so the board matches the RESOLVED lead exactly as `GET /deals` does.
   *
   * ⚠️ *SUPERSEDED 2026-10-28: "SINGLE-VALUED … sending a list here is a 422."*
   * The deals board takes a CSV under the same key now, like every id filter.
   */
  if (filters.accountLeadId.length > 0) {
    boardParams.accountLeadId = filters.accountLeadId.join(',');
  }
  /*
   * ⚠️ `filters.projectId` is DELIBERATELY NOT SENT. `GET /deals` takes it; the
   * deals board does not declare it and the schema is `.strict()`, so this one
   * key would 422 the whole board. The notice below the badge says the board is
   * unfiltered by it rather than letting the two views disagree in silence.
   */

  /*
   * ⚠️ *SUPERSEDED 2026-10-09: "The hook's headers, unwrapped."* The wrapper is
   * back for the SORT, which is a server query: a new order from page 3 would
   * ask for page 3 of a re-ordered list, so choosing one resets to page 1. The
   * FILTER half stays unwrapped, for the reason below.
   *
   * ⚠️ *SUPERSEDED: a wrapper that reset the pager on a `scope === 'server'`
   * funnel change, "so no request is ever fired for page 3 of a result set that
   * no longer exists".* **Every column here is page-scoped now**, and a
   * page-scoped column deliberately does NOT reset: it filters rows already
   * loaded, so jumping to page 1 would throw away the very rows being filtered.
   * With nothing left to reset the wrapper was an identity function whose
   * comment described a branch that could no longer be reached.
   *
   * ⚠️ **Restore it with any future server-scoped column** — the reset belongs
   * in the event that changes the query, never in an effect watching it.
   */
  function headerFor(key: string): FilterableTHProps {
    const props = table.headerFor(key);
    const filter = props.filter;
    const sortControl = filter?.sort;
    if (!filter || !sortControl) return props;
    return {
      ...props,
      filter: {
        ...filter,
        sort: {
          direction: sortControl.direction,
          onSort: (direction) => {
            sortControl.onSort(direction);
            setPage(1);
          },
        },
      },
    };
  }

  function setFilter(key: IdFilter | 'currency' | typeof QUOTE_SIGNED_KEY, value: string) {
    setPage(1);
    const next = new URLSearchParams(searchParams);
    if (value === '') next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  }

  /**
   * Writes the closing-date pair, keeping in the URL only the halves that could
   * actually be sent and holding the rest as a draft with a message — so the URL
   * never carries a bound the request does not. Promotion works both ways:
   * fixing the "from" date puts a previously withheld "to" back into the URL.
   */
  function applyDates(after: string, before: string) {
    // The list IS filtered by these, so a stale page 5 would read as an empty
    // pipeline the moment the range narrows.
    setPage(1);
    const afterOk = after !== '' && isRealDate(after);
    const beforeOk = before !== '' && isRealDate(before);
    // The message lands on the "to" field, so the "to" bound is the half held
    // back — these two decisions must agree, or the error names a field that is
    // being applied anyway.
    const sendAfter = afterOk;
    const sendBefore = beforeOk && !(afterOk && after > before);

    const next = new URLSearchParams(searchParams);
    if (sendAfter) next.set('closingDateAfter', after);
    else next.delete('closingDateAfter');
    if (sendBefore) next.set('closingDateBefore', before);
    else next.delete('closingDateBefore');
    setSearchParams(next, { replace: true });

    setDateDraft({
      closingDateAfter: sendAfter || after === '' ? null : after,
      closingDateBefore: sendBefore || before === '' ? null : before,
    });
  }

  function setDateFilter(key: DateFilter, value: string) {
    applyDates(
      key === 'closingDateAfter' ? value : afterInput,
      key === 'closingDateBefore' ? value : beforeInput,
    );
  }

  function clearFilters() {
    setSearch('');
    setPage(1);
    table.clearAll();
    // The drafts are not in the URL, so wiping the query string alone would
    // leave a rejected date sitting in the field with its error under it.
    setDateDraft({ closingDateAfter: null, closingDateBefore: null });
    // The view is not a filter, so `view` is carried over and everything else
    // goes. ⚠️ The BOARD is the default since 2026-09-11, so it is the TABLE
    // that has to be re-spelled here; dropping the key would silently swap the
    // view. **This flipped with `parseView` and must always flip with it.**
    const next = new URLSearchParams();
    if (!showBoard) next.set('view', 'table');
    setSearchParams(next, { replace: true });
  }

  function setView(next: DealsView) {
    const params = new URLSearchParams(searchParams);
    // The board is the default, so it is spelled by the key's absence rather
    // than by `?view=board` — one canonical URL per view. ⚠️ This has to flip
    // with the default in `parseView`, or the page writes `?view=board` for
    // ever and a reader who picks Table gets it back on the next arrival.
    if (next === 'table') params.set('view', 'table');
    else params.delete('view');
    setSearchParams(params, { replace: true });
  }

  /*
   * ⚠️ Deleting a deal cascades its line items away with no warning and no count
   * in the response, so the count is fetched first and named in the dialog. The
   * API will not tell the user; this is the only chance to.
   */
  async function openDelete(deal: Deal) {
    setDeleteError(null);
    setPendingLineCount(null);
    setPendingDelete(deal);
    try {
      const basket = await api.listDealProducts(deal.id);
      setPendingLineCount(basket.data.length);
    } catch {
      // A failed count must not block the delete — the dialog falls back to
      // the generic warning rather than claiming there are none.
      setPendingLineCount(null);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteDeal(pendingDelete.id);
      setPendingDelete(null);
      if (!showAll && rows.length === 1 && page > 1) setPage((p) => p - 1);
      else await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 404) {
        setPendingDelete(null);
        if (!showAll && rows.length === 1 && page > 1) setPage((p) => p - 1);
        else await load();
      } else if (caught instanceof ApiError && caught.status === 403) {
        /*
         * A role can change under a live token, so the button can be offered
         * honestly and still be refused. A 403 body carries no `details`, so
         * this is a banner and never a field error. `denialReason` returns null
         * while the ability still believes the delete was allowed.
         */
        setDeleteError(
          denialReason(ability, 'delete', 'deals') ??
            'Your account no longer has permission to delete deals.',
        );
      } else {
        setDeleteError(caught instanceof Error ? caught.message : 'Delete failed');
      }
    } finally {
      setDeleting(false);
    }
  }

  /*
   * Closing takes `new=1` out of the URL with it. Left there, a refresh or a
   * back-navigation reopens a form the user just dismissed. `replace` so the
   * dismissal does not become a history entry of its own.
   */
  function closeForm() {
    setFormState(null);
    if (!searchParams.has('new')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('new');
    setSearchParams(next, { replace: true });
  }

  function handleSaved() {
    const wasCreate = !activeForm?.deal;
    closeForm();
    /*
     * The board holds its own copy of the records, so it is told to refetch.
     * Bumped on an edit too: the card carries the name, amount and closing date,
     * and a stage change in the form moves the card to another column outright.
     */
    setBoardRefresh((token) => token + 1);
    if (wasCreate && !showAll && page !== 1) setPage(1);
    else void load();
  }

  /**
   * What the column funnels did.
   *
   * ⚠️ *SUPERSEDED: three branches, "worded so neither half claims the other's
   * reach: one re-queries and one does not".* **Nothing here re-queries any
   * more** — Account lead was the only server-scoped column and the bank owns
   * that key now — so one sentence is true of all of them again. Restore the
   * branching only alongside a column that really does reach past the page.
   */
  const loadedDeals = `${rows.length} ${rows.length === 1 ? 'deal' : 'deals'}`;
  const columnScopeSentence = `Applies to the ${loadedDeals} on this page only.`;
  const columnHiddenSentence =
    hiddenByColumns > 0
      ? ` ${hiddenByColumns} loaded ${hiddenByColumns === 1 ? 'deal is' : 'deals are'} hidden.`
      : '';

  const tableCountLabel = initialLoad
    ? 'Loading…'
    : `${total} ${total === 1 ? 'deal' : 'deals'}${search ? ` matching “${search}”` : ''}`;
  /*
   * The board keeps its own per-column counts and has no page, so a row count
   * here would describe a request that was never sent.
   */
  const boardFilterCount =
    // ⚠️ `BOARD_ID_FILTERS`, not `ID_FILTERS`: `projectId` is never sent to the
    // board, so counting it would claim the board had been narrowed by a filter
    // it never received. `accountLeadId` IS in that list and IS sent, so it
    // counts — which is what stops a defaulted board reading as an empty
    // pipeline rather than as a filtered one.
    BOARD_ID_FILTERS.filter((key) => filters[key].length > 0).length +
    (currency !== null ? 1 : 0) +
    (closingAfter !== null ? 1 : 0) +
    (closingBefore !== null ? 1 : 0);
  const countLabel = showBoard
    ? `Grouped by stage${search ? ` · matching “${search}”` : ''}${
        boardFilterCount > 0
          ? ` · ${boardFilterCount} ${boardFilterCount === 1 ? 'filter' : 'filters'} applied`
          : ''
      }`
    : tableCountLabel;

  const newDealButton = canCreate ? (
    <Button variant="primary" leadingIcon={<PlusIcon />} onClick={() => setFormState({})}>
      New deal
    </Button>
  ) : null;

  /*
   * ⚠️ Below every hook, so the early return cannot change the hook order. The
   * refusal is rendered here rather than by the route, which carries no guard.
   */
  if (!canReadModule) {
    return (
      <>
        <PageHeader eyebrow="Operations" title="Deals" />
        <Panel className="min-w-0">
          <EmptyState
            title="This page isn’t available to your account"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Operations"
        title="Deals"
        actions={
          <>
            <ViewToggle view={view} onChange={setView} />
            {newDealButton}
          </>
        }
      />

      <Panel>
        <PanelHeader title="All deals" description={countLabel} />

        {/*
          * ⚠️ This bank does NOT belong in `PanelHeader`'s `actions` slot — that
          * slot is `shrink-0`, so the controls can neither shrink nor wrap and
          * the page blows out sideways at 768px. Its own full-width row wraps.
          *
          * `items-end`, not `items-center`: the closing-date pair is the only
          * group carrying visible labels, and aligning on the bottom edge keeps
          * every control's box on one line with the labels above it.
          *
          * Every track is `min-w-0` and full width below `sm`, so at 320px the
          * bank is a single stack rather than a row that overflows.
          */}
            <div className="flex flex-wrap items-end gap-2 border-b border-border px-4 py-3">
              {/*
                * ✅ **The three reference-master filters are SEARCHABLE
                * Comboboxes — owner's decision 2026-09-13.** *SUPERSEDED: three
                * `ui/Select`s, each with an inline `<option>` list.* The owner
                * chose one filter shape across the bank over the "a handful of
                * options needs no search box" reading, so these now match the
                * currency and account-lead controls beside them rather than
                * being the two-of-five that did not.
                *
                * ⚠️ **`disabled` became `loading`, and that is not a rename.** A
                * disabled control says "not for you"; `Combobox`'s own loading
                * state says "the list is on its way" — and it stays operable, so
                * a value already in the URL can be cleared while the master is
                * still arriving. The pickers load as one `Promise.allSettled`,
                * so all three lift together exactly as they did before.
                *
                * The `invalid` spread is conditional so an absent flag is an
                * absent prop rather than an explicit `false` — the notices under
                * this bank are what carry the words, here as before. Never
                * colour as the only signal.
                */}
              <div className="w-full min-w-0 sm:w-40">
                <Field label="Filter by stage" hideLabel>
                  {({ id }) => (
                    <MultiCombobox
                      id={id}
                      values={filters.stageId.map(String)}
                      // `[]` joins to '', which deletes the key.
                      onChange={(values) => setFilter('stageId', values.join(','))}
                      options={stagePickerOptions}
                      maxVisibleChips={1}
                      summaryNoun={{ singular: 'stage', plural: 'stages' }}
                      placeholder="All stages"
                      searchPlaceholder="Search stages…"
                      loading={pickers.loading}
                      {...(pickers.failedBy.stages ? { invalid: true } : {})}
                    />
                  )}
                </Field>
              </div>
              <div className="w-full min-w-0 sm:w-40">
                <Field label="Filter by type" hideLabel>
                  {({ id }) => (
                    <MultiCombobox
                      id={id}
                      values={filters.dealTypeId.map(String)}
                      onChange={(values) => setFilter('dealTypeId', values.join(','))}
                      options={dealTypePickerOptions}
                      maxVisibleChips={1}
                      summaryNoun={{ singular: 'type', plural: 'types' }}
                      placeholder="All types"
                      searchPlaceholder="Search types…"
                      loading={pickers.loading}
                      {...(pickers.failedBy.dealTypes ? { invalid: true } : {})}
                    />
                  )}
                </Field>
              </div>
              <div className="w-full min-w-0 sm:w-40">
                <Field label="Filter by lead source" hideLabel>
                  {({ id }) => (
                    <MultiCombobox
                      id={id}
                      values={filters.leadSourceId.map(String)}
                      onChange={(values) => setFilter('leadSourceId', values.join(','))}
                      options={leadSourcePickerOptions}
                      maxVisibleChips={1}
                      summaryNoun={{ singular: 'source', plural: 'sources' }}
                      placeholder="All sources"
                      searchPlaceholder="Search lead sources…"
                      loading={pickers.loading}
                      {...(pickers.failedBy.leadSources ? { invalid: true } : {})}
                    />
                  )}
                </Field>
              </div>
              <div className="w-full min-w-0 sm:w-40">
                <Field label="Filter by currency" hideLabel>
                  {({ id }) => (
                    <Combobox
                      id={id}
                      // The CODE is the value: a deal stores CHAR(3), there is
                      // no id to send, and the list endpoint refuses 'usd'.
                      value={currency ?? ''}
                      onChange={(value) => setFilter('currency', value)}
                      options={currencyPickerOptions}
                      emptyOptionLabel="All currencies"
                      placeholder="All currencies"
                      searchPlaceholder="Search currencies…"
                      loading={currencies.loading}
                      {...(currencies.failed ? { invalid: true } : {})}
                    />
                  )}
                </Field>
              </div>
              {/*
                * Quote signed — TABLE VIEW ONLY: the deals board does not take
                * the key (see `QUOTE_SIGNED_KEY`), and a control that changes
                * nothing on screen is worse than none. A value already in the
                * URL is named on the board by the notice below the badges.
                */}
              {!showBoard && (
                <div className="w-full min-w-0 sm:w-40">
                  <Field label={`Filter by ${QUOTE_SIGNED_LABEL.toLowerCase()}`} hideLabel>
                    {({ id }) => (
                      <Combobox
                        id={id}
                        value={quoteSigned ?? ''}
                        onChange={(value) => setFilter(QUOTE_SIGNED_KEY, value)}
                        options={QUOTE_SIGNED_PICKER_OPTIONS}
                        emptyOptionLabel="Signed or not"
                        placeholder={QUOTE_SIGNED_LABEL}
                      />
                    )}
                  </Field>
                </div>
              )}
              {/*
                * 🔴 **The Account lead filter — in the BANK, not on a table
                * header, and that is the whole point of it.** The board is the
                * default view; a column funnel lives on a `<th>`, so the one
                * control meaning "deals attached to me" would have been
                * unreachable exactly where most people now land. It is also
                * what carries the default, which has to be VISIBLE to be
                * clearable.
                *
                * `sm:w-48` rather than the `sm:w-40` its four neighbours take:
                * these options are people's names, which are longer than
                * "All stages" and do not truncate gracefully.
                *
                * ⛔ **No hint, no note, and none is missing.** What this narrows
                * by is the control's own label and its selected value; the
                * board says "N filters applied" beside it and the table's
                * footer says how many rows. A sentence explaining that it
                * starts on you would be helper text attached to a control —
                * and it would rot the first time the default changes.
                */}
              <div className="w-full min-w-0 sm:w-48">
                <Field label="Filter by account lead" hideLabel>
                  {({ id }) => (
                    <MultiCombobox
                      id={id}
                      /*
                       * The RESOLVED lead's id — the deal's own override where
                       * it has one, otherwise its company's. Both `GET /deals`
                       * and the deals board match it that way, so the two views
                       * and the Account lead column agree by construction.
                       */
                      values={filters.accountLeadId.map(String)}
                      onChange={(values) => setFilter('accountLeadId', values.join(','))}
                      options={accountLeadPickerOptions}
                      maxVisibleChips={1}
                      summaryNoun={{ singular: 'account lead', plural: 'account leads' }}
                      placeholder="All account leads"
                      searchPlaceholder="Search people…"
                      loading={staff.loading}
                      /*
                       * Explained in the notice below the bank, never by colour
                       * alone. A failed `/users` read leaves the list narrower
                       * rather than empty — the leads the loaded rows name are
                       * still in it — so this marks a degraded list, not a dead
                       * control.
                       */
                      {...(staff.failed ? { invalid: true } : {})}
                    />
                  )}
                </Field>
              </div>
              {/*
                * Two bounds, one labelled group — a fieldset so a screen reader
                * announces "Closing date, from" instead of a bare date field.
                * The per-input labels stay visible because a date box with
                * nothing on it cannot say which end of the range it is. The two
                * wrap onto separate lines at 320px.
                */}
              <fieldset className="m-0 w-full min-w-0 border-0 p-0 sm:w-80">
                <legend className="mb-1.5 p-0 text-xs font-medium text-fg-secondary">
                  Closing date
                </legend>
                <div className="flex min-w-0 flex-wrap items-start gap-2">
                  <div className="min-w-0 flex-1 basis-36">
                    <Field label="From" error={afterError}>
                      {({ id, 'aria-describedby': describedBy }) => (
                        <Input
                          id={id}
                          type="date"
                          aria-describedby={describedBy}
                          invalid={Boolean(afterError)}
                          value={afterInput}
                          onChange={(e) => setDateFilter('closingDateAfter', e.target.value)}
                        />
                      )}
                    </Field>
                  </div>
                  <div className="min-w-0 flex-1 basis-36">
                    <Field label="To" error={beforeError}>
                      {({ id, 'aria-describedby': describedBy }) => (
                        <Input
                          id={id}
                          type="date"
                          aria-describedby={describedBy}
                          invalid={Boolean(beforeError)}
                          /*
                           * No `max`: a deal usually closes in the future, so
                           * today is not a ceiling. ⚠️ One would have to be built
                           * from LOCAL calendar parts — `toISOString()` is UTC
                           * and hands back tomorrow east of Greenwich.
                           */
                          value={beforeInput}
                          onChange={(e) => setDateFilter('closingDateBefore', e.target.value)}
                        />
                      )}
                    </Field>
                  </div>
                </div>
              </fieldset>
              <div className="w-full min-w-0 sm:w-56">
                <Field label="Search deals" hideLabel>
                  {({ id }) => (
                    <Input
                      id={id}
                      type="search"
                      // The API matches deal_name only.
                      placeholder="Search deal name…"
                      leadingIcon={<SearchIcon />}
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                      }}
                    />
                  )}
                </Field>
              </div>
              {/*
                * ⛔ **TABLE VIEW ONLY, and `md` and up only.** This page OPENS ON
                * THE BOARD, which has no columns to manage; and below `md` the
                * table is replaced by cards, which have none either. A control
                * whose only outcome is nothing is worse than no control — the
                * rule that keeps Xero's `sync` affordance ABSENT rather than
                * inert. It stays in the bank rather than moving next to the
                * table so that it is still reachable when the column filters
                * have hidden every row and the empty state is showing.
                *
                * ⚠️ The breakpoint is on a WRAPPER, not on the trigger's own
                * class: `Button` is already `inline-flex`, and `hidden` and
                * `inline-flex` are the same CSS property, so a
                * `hidden md:inline-flex` there would be racing the emitted
                * stylesheet's order rather than overriding anything.
                */}
              {!showBoard && (
                <div className="hidden md:block">
                  <ColumnManager
                    layout={columnLayout}
                    labelOf={labelOf}
                    lockedKeys={LOCKED_KEYS}
                    lockReasonOf={lockReasonOf}
                    pinnedKeys={PINNED_KEYS}
                  />
                </div>
              )}
        </div>

        {/*
          * What the filter bank above cannot tell you about itself. A dropdown
          * whose request failed renders identically to a master with no rows —
          * empty list, no error, nothing to retry — and a picker silently
          * missing its 101st option is indistinguishable from that option not
          * existing. Deals themselves are unaffected: every filter is applied
          * server-side on the id in the URL, so a deep link to a value missing
          * from a dropdown still narrows the list correctly.
          */}
        {(failedPickers.length > 0 || truncatedPickers.length > 0 || staff.failed) && (
          <div className="grid gap-1 border-b border-border px-4 py-2">
            {/*
              * Its own sentence rather than a name in the list above, because
              * what happens to THIS control on a failed read is different:
              * it narrows rather than empties. Saying "empty rather than
              * complete" over a dropdown that still offers half a dozen people
              * would send somebody hunting for a fault they can see is not
              * there. A refusal-class sentence, and an explicit keeper under
              * the no-helper-text rule — it names a state the reader has no
              * other way to see.
              */}
            {staff.failed && (
              <p className="m-0 text-xs text-danger">
                The staff list could not be loaded, so Account lead offers only the people named
                on the deals loaded here rather than everyone. Reload to try again.
              </p>
            )}
            {failedPickers.length > 0 && (
              <p className="m-0 text-xs text-danger">
                The {joinList(failedPickers)} {failedPickers.length === 1 ? 'list' : 'lists'} could
                not be loaded, so{' '}
                {failedPickers.length === 1 ? 'that dropdown is' : 'those dropdowns are'} empty
                rather than complete. Reload to try again.
              </p>
            )}
            {truncatedPickers.length > 0 && (
              <p className="m-0 text-xs text-fg-muted">
                Showing only the first page of {joinList(truncatedPickers)} — some options are
                missing.
              </p>
            )}
          </div>
        )}

        {(filters.companyId.length > 0 ||
          filters.contactId.length > 0 ||
          filters.projectId.length > 0) && (
          <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
            <span className="text-xs text-fg-muted">Also filtered by</span>
            {filters.companyId.map((companyId) => (
              <Badge key={`company-${companyId}`} tone="neutral" variant="outline">
                {(companyId === companyFilterId ? companyName : null) ?? `Company #${companyId}`}
              </Badge>
            ))}
            {filters.contactId.map((contactId) => (
              <Badge key={`contact-${contactId}`} tone="neutral" variant="outline">
                Contact #{contactId}
              </Badge>
            ))}
            {/* Named where a row could name it, by id where none could — the
                list is usually empty under this filter. */}
            {filters.projectId.map((projectId) => {
              const projectName = projectNameOf(projectId);
              return (
                <Badge key={`project-${projectId}`} tone="neutral" variant="outline">
                  {projectName === null ? `Project #${projectId}` : `Project: ${projectName}`}
                </Badge>
              );
            })}
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear
            </Button>
          </div>
        )}

        {/*
          * The one filter the board cannot take, said next to the badge that
          * would otherwise imply it applied to both views — a filtered table and
          * an unfiltered board under one heading is the "it looks like it
          * worked" failure.
          */}
        {showBoard && filters.projectId.length > 0 && (
          <div className="border-b border-border px-4 py-2">
            <p className="m-0 text-xs text-fg-muted">
              The board is <strong className="font-semibold">not</strong> narrowed by the project
              filter. Switch to Table for this project&rsquo;s deals.
            </p>
          </div>
        )}
        {showBoard && quoteSigned !== null && (
          <div className="border-b border-border px-4 py-2">
            <p className="m-0 text-xs text-fg-muted">
              The board is <strong className="font-semibold">not</strong> narrowed by the{' '}
              {QUOTE_SIGNED_LABEL.toLowerCase()} filter. Switch to Table to apply it.
            </p>
          </div>
        )}

        {/*
          * Column filters are set from the table headers at `md` and up and from
          * the FilterSheet below it, so their state is explained here — a filter
          * set on a wide screen must not go on silently hiding rows on a narrow
          * one. The *clear* button is the wide-screen half only: below `md` the
          * sheet carries its own, and two same-labelled buttons a screen apart
          * read as two different scopes.
          *
          * ⚠️ They are not all client-side. Account lead is `scope: 'server'` and
          * narrows the whole pipeline, which is why the sentence comes from
          * `columnScopeSentence` rather than asserting page scope for all.
          */}
        {!showBoard && table.activeCount > 0 && (
          <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
            <p className="m-0 min-w-0 text-xs text-fg-muted">
              {table.activeCount} column {table.activeCount === 1 ? 'filter' : 'filters'} applied.{' '}
              {columnScopeSentence}
              {columnHiddenSentence}
            </p>
            <span className="hidden md:contents">
              <Button variant="ghost" size="sm" onClick={table.clearAll}>
                Clear column filters
              </Button>
            </span>
          </div>
        )}

        {/*
          * ⚠️ **REMOVED 2026-09-11, and it is a removal rather than an
          * omission.** *SUPERSEDED: a board-only notice reading "The Account
          * lead column filter narrows this board too", with its own clear
          * button.* It existed because the funnel was on a table header and
          * reached the board anyway — a filter narrowing what you were looking
          * at with no control on screen saying why and no way back out.
          *
          * **Account lead is a control in the bank above now, visible in both
          * views with its value showing and clearable in place**, so the
          * sentence would name a state the reader can already see. The column
          * funnels that remain are all page-scoped and the board loads no rows
          * for them to hide, which is why nothing replaces it.
          *
          * ⚠️ **Do not reinstate this alongside any future server-scoped
          * column** without also giving that column a control the board can
          * reach — the notice was the patch, not the fix.
          */}

        <p role="status" aria-live="polite" className="sr-only">
          {countLabel}
        </p>

        {/* The list error, not the board's — `KanbanBoard` renders its own, with
            its own retry, and the two requests fail independently. */}
        {!showBoard && error && (
          <div className="border-b border-border px-4 py-3">
            <Notice tone="danger" role="alert" title={<>Couldn&rsquo;t load deals.</>}>
              <span className="[overflow-wrap:anywhere]">{error}</span>
            </Notice>
          </div>
        )}

        {/*
          * `min-w-0` because the board is a horizontally scrolling flex track:
          * without it the widest column sets this container's floor and takes
          * the page sideways at 320px. `canMove` is `canUpdate`, not `isAdmin` —
          * `PATCH /deals/:id/stage` is `requireRole(...WRITE_ROLES)`, so a basic
          * user may drag and only a viewer may not.
          */}
        {showBoard && (
          <div className="min-w-0 px-4 py-3">
            <KanbanBoard
              board="deals"
              params={boardParams}
              hrefFor={(id) => `/deals/${id}`}
              canMove={canUpdate}
              enabled={showBoard}
              confirmMove={confirmBoardMove}
              /*
               * The SAME modal the header button and the table rows open —
               * `setFormState({})` is the one way this page opens a create form,
               * so there is no second piece of state to drift. Omitted outright
               * without `canCreate`, rather than showing a viewer a waiting 403.
               */
              create={
                canCreate
                  ? {
                      label: 'New deal',
                      onCreate: () => setFormState({}),
                      refreshToken: boardRefresh,
                    }
                  : undefined
              }
              /*
               * ⚠️ **SUPERSEDED 2026-10-25 (owner decision 3, quote PDF
               * preview): the board card now carries ONE `cardAction`, the
               * deal's quote PDF picker.** It is the only card control; the
               * "+" create menu, Edit and Delete stay table-only. Omitted
               * outright without `quotes.read`, so a card never reserves
               * clearance for a control that renders nothing. The history
               * below explains why there was none before.
               */
              cardAction={
                canPreviewQuotes
                  ? (card) => (
                      <DealQuotesPdfMenu
                        dealId={card.id}
                        dealName={card.title?.trim() || `Deal #${card.id}`}
                        onPreview={quotePreview.open}
                        variant="card"
                      />
                    )
                  : undefined
              }
              /*
               * *SUPERSEDED:* NO `cardAction`, deliberately — and it stays that way on the
               * owner's decision, which is why the reasoning below is CORRECTED
               * rather than left standing.
               *
               * ⚠️ **This note used to read: "The row's only added action is
               * 'open the deal', and `hrefFor` above already makes the WHOLE
               * CARD a stretched `<Link>` to that address … Add `cardAction`
               * back for any action the card cannot already reach."** That was
               * true until the row gained its "+" menu, and it stopped being
               * true in the direction that argues for the OPPOSITE of what was
               * decided: raising a quote or an invoice IS an action the card
               * cannot already reach, so the old closing sentence now reads as
               * an instruction to add the control the owner asked to leave off.
               *
               * What survives unchanged: `hrefFor` makes the whole card a
               * stretched `<Link>` to the deal, so a card button for "open"
               * would be a second control for one action, costing the title
               * `pr-16` of clearance (`KanbanCard`'s `controlCount`) for
               * nothing. The board is a pipeline view, and the two views are
               * deliberately not required to offer the same controls.
               */
            />
          </div>
        )}

        {lostMove && (
          <DealLostReasonDialog
            open
            deal={lostMove.deal}
            stage={lostMove.stage}
            onCancel={() => {
              lostMove.resolve('cancel');
              setLostMove(null);
            }}
            onSaved={() => {
              lostMove.resolve('handled');
              setLostMove(null);
            }}
          />
        )}

        {!showBoard && (
          <div
            aria-busy={loading || undefined}
            className={cx(
              'transition-opacity duration-150 ease-out motion-reduce:transition-none',
              loading && result && 'opacity-60',
            )}
          >
            <div className="md:hidden">
              {/*
                * A card has no header row, so the table-header funnels have
                * nowhere to hang below `md`. `FilterSheet` is their mobile form,
                * handed the SAME `headerFor` and `clearAll` from the one
                * `useTableFilters` call above — one state store, so a filter set
                * here is the filter the table shows when the viewport widens.
                */}
              {/* The bar owns the border and padding; FilterSheet's className lands on its
                  trigger Button, so styling a bar through it would double the padding. */}
              <div className="border-b border-border px-4 py-2">
                <FilterSheet
                  keys={DEAL_COLUMN_KEYS}
                  headerFor={headerFor}
                  activeCount={table.activeCount}
                  clearAll={table.clearAll}
                />
              </div>
              {initialLoad ? (
                <ListSkeleton />
              ) : visibleRows.length === 0 ? (
                <DealsEmptyState
                  filtered={anyFilter}
                  currencyFiltered={currency !== null}
                  projectFiltered={filters.projectId.length > 0}
                  columnsHidAll={rows.length > 0}
                  onClearColumns={table.clearAll}
                  onClear={clearFilters}
                  hasError={Boolean(error)}
                  action={newDealButton}
                />
              ) : (
                <ul className="m-0 list-none p-0">
                  {visibleRows.map((deal) => (
                    <li key={deal.id} className="border-b border-border px-4 py-3 last:border-b-0">
                      <div className="flex items-start justify-between gap-3">
                        <DealName deal={deal} />
                        <DealStageBadge stage={deal.stage} />
                      </div>
                      <div className="mt-2 flex items-center justify-between gap-3 text-xs">
                        {/* `dealValue`, never `deal.amount` — the lines win
                            when there are lines. Same figure as the table's
                            Amount column, from the same function, so the two
                            views cannot quote different money. */}
                        <span className="text-fg-secondary tabular">
                          <MoneyCell value={dealValue(deal)} />
                          {deal.currency ? ` ${deal.currency}` : ''}
                        </span>
                        <span className="text-fg-muted tabular">
                          {deal.closingDate ? formatDateOnly(deal.closingDate) : 'No close date'}
                        </span>
                      </div>
                      <div className="mt-2">
                        <AccountLeadCell deal={deal} />
                      </div>
                      {/* Same field as the table's Quote signed column; absent
                          only when an older API sent no key. */}
                      {deal.quoteSigned !== undefined && (
                        <div className="mt-2 flex items-center justify-between gap-3 text-xs">
                          <span className="text-fg-muted">{QUOTE_SIGNED_LABEL}</span>
                          <QuoteSignedCell deal={deal} />
                        </div>
                      )}
                      {/*
                        No `actionCount > 0` guard: opening the deal and the
                        tasks-and-notes sheet are ungated, so a permission test
                        here would hide controls that answer to no permission. It
                        wraps, so four buttons fold onto a second line at 320px.
                      */}
                      <div className="mt-2.5 flex flex-wrap items-center justify-end gap-2">
                        {/* Ungated, like the sheet beside it: every role may
                            read a deal. */}
                        <Button
                          variant="secondary"
                          size="sm"
                          leadingIcon={<DealIcon />}
                          onClick={() => navigate(`/deals/${deal.id}`)}
                        >
                          Open
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          leadingIcon={<ActivityIcon />}
                          onClick={() => setActivityFor(deal)}
                        >
                          Tasks
                        </Button>
                        <DealQuotesPdfMenu
                          dealId={deal.id}
                          dealName={deal.dealName}
                          onPreview={quotePreview.open}
                          variant="card"
                        />
                        {canUpdate && (
                          <Button
                            variant="secondary"
                            size="sm"
                            leadingIcon={<PencilIcon />}
                            onClick={() => setFormState({ deal })}
                          >
                            Edit
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            variant="danger"
                            size="sm"
                            leadingIcon={<TrashIcon />}
                            onClick={() => void openDelete(deal)}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="hidden md:block">
              {initialLoad ? (
                <DealsTable minWidth={tableMinWidth}>
                  <DealsTableHead
                    actionCount={actionCount}
                    headerFor={headerFor}
                    visibleKeys={visibleColumnKeys}
                    headRowRef={headRowRef}
                    frozenSideOf={columnLayout.frozenSideOf}
                    frozenOffsets={frozenOffsets}
                  />
                  <TableSkeleton
                    actionCount={actionCount}
                    visibleKeys={visibleColumnKeys}
                    frozenSideOf={columnLayout.frozenSideOf}
                    frozenOffsets={frozenOffsets}
                  />
                </DealsTable>
              ) : visibleRows.length === 0 ? (
                <DealsEmptyState
                  filtered={anyFilter}
                  currencyFiltered={currency !== null}
                  projectFiltered={filters.projectId.length > 0}
                  columnsHidAll={rows.length > 0}
                  onClearColumns={table.clearAll}
                  onClear={clearFilters}
                  hasError={Boolean(error)}
                  action={newDealButton}
                />
              ) : (
                <DealsTable minWidth={tableMinWidth}>
                  <DealsTableHead
                    actionCount={actionCount}
                    headerFor={headerFor}
                    visibleKeys={visibleColumnKeys}
                    headRowRef={headRowRef}
                    frozenSideOf={columnLayout.frozenSideOf}
                    frozenOffsets={frozenOffsets}
                  />
                  <TBody>
                    {visibleRows.map((deal) => (
                      <TR key={deal.id} interactive>
                        {visibleColumnKeys.map((key) =>
                          key === ACTIONS_KEY ? (
                            <TD key={key} numeric pinned>
                              {/*
                                No "no actions" branch: the two controls below are
                                ungated, so `actionCount` is never 0 and an em dash
                                carrying `denialReason(...)` would be dead code
                                asserting a state the page cannot enter.
                              */}
                              <div className="flex justify-end gap-1.5">
                                {/*
                                  Ungated: reading a deal is open to every role,
                                  `viewer` included. A `Button` and not a `Link`
                                  because a `Link` here would be a hand-rolled
                                  button; the row's name cell is the real link, so
                                  opening in a new tab is not lost.
                                */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  iconOnly
                                  leadingIcon={<DealIcon />}
                                  aria-label={`Open ${deal.dealName}`}
                                  title={`Open ${deal.dealName}`}
                                  onClick={() => navigate(`/deals/${deal.id}`)}
                                />
                                {/* Ungated: the task and note routers carry no
                                    role guard. */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  iconOnly
                                  leadingIcon={<ActivityIcon />}
                                  aria-label={`Tasks and notes for ${deal.dealName}`}
                                  title={`Tasks and notes for ${deal.dealName}`}
                                  onClick={() => setActivityFor(deal)}
                                />
                                {/* ⚠️ **Raises nothing by itself — it navigates to
                                    THIS DEAL**, which opens the prefilled
                                    conversion form there. *SUPERSEDED: "it opens a
                                    create form on the quotes or invoices list."*
                                    That was never where it went: `hrefFor` builds
                                    `/deals/{id}?create=…`, because both conversion
                                    surfaces need the whole `Deal` and a list row
                                    does not carry one. Absent entirely for
                                    somebody who can create neither, rather than
                                    present and disabled. */}
                                <DealQuotesPdfMenu
                                  dealId={deal.id}
                                  dealName={deal.dealName}
                                  onPreview={quotePreview.open}
                                  variant="row"
                                />
                                {createTargets.length > 0 && (
                                  <CreateFromDealMenu deal={deal} targets={createTargets} />
                                )}
                                {canUpdate && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    iconOnly
                                    leadingIcon={<PencilIcon />}
                                    aria-label={`Edit ${deal.dealName}`}
                                    title={`Edit ${deal.dealName}`}
                                    onClick={() => setFormState({ deal })}
                                  />
                                )}
                                {canDelete && (
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    iconOnly
                                    leadingIcon={<TrashIcon />}
                                    aria-label={`Delete ${deal.dealName}`}
                                    title={`Delete ${deal.dealName}`}
                                    onClick={() => void openDelete(deal)}
                                  />
                                )}
                              </div>
                            </TD>
                          ) : (
                            <TD
                              key={key}
                              numeric={NUMERIC_KEYS.has(key)}
                              className={BODY_CELL_CLASSES[key]}
                              frozen={columnLayout.frozenSideOf(key)}
                              frozenOffset={frozenOffsets[key]}
                            >
                              <DealCell columnKey={key} deal={deal} />
                            </TD>
                          ),
                        )}
                      </TR>
                    ))}
                  </TBody>
                </DealsTable>
              )}
            </div>
          </div>
        )}

        {/* Pagination belongs to the list request. The board has no pages —
            `cardsPerColumn` bounds each column and the counts are its own. */}
        {!showBoard && result && (
          // `ListPager` IS the PanelFooter. The column-filter count rides on
          // the range line, so that case supplies the whole line itself.
            <ListPager
              {...(total > 0 && hiddenByColumns > 0
                ? {
                    summary: `${
                      showAll
                        ? truncated
                          ? `Showing the first ${lastRow} of ${total}`
                          : `Showing all ${total}`
                        : `Showing ${firstRow}–${lastRow} of ${total}`
                    } · ${hiddenByColumns} hidden by column filters`,
                  }
                : {})}
              page={showAll ? 1 : page}
              totalPages={showAll ? 1 : totalPages}
              total={total}
              firstRow={firstRow}
              lastRow={lastRow}
              perPage={PER_PAGE}
              showAll={showAll}
              loading={loading}
              onPage={setPage}
              onToggleShowAll={setShowAll}
              noun="deals"
              truncated={showAll && truncated}
            />
        )}
      </Panel>

      <DealFormModal
        open={activeForm !== null}
        onClose={closeForm}
        deal={activeForm?.deal}
        /*
         * Locked only for a form opened FOR a company, and only on create:
         * editing a deal from a company-filtered list must still be able to move
         * it to another company. `filterCompany` can go null under an open form
         * if the filter is cleared behind it, and the picker comes back.
         */
        lockedCompany={
          activeForm?.lockCompany && !activeForm.deal && filterCompany ? filterCompany : undefined
        }
        onSaved={handleSaved}
      />

      {/*
        One sheet for the whole table, not one per row — a row only says WHICH
        deal it is for. `open` is derived from that, so the heading always has a
        name behind it.
      */}
      <ActivityDrawer
        open={activityFor !== null}
        onClose={() => setActivityFor(null)}
        target={{ type: 'deal', id: activityFor?.id ?? 0 }}
        recordLabel={activityFor?.dealName.trim() || `Deal #${activityFor?.id ?? ''}`}
        viewerId={user?.id ?? null}
        viewerTimeZone={viewerZone(user?.timezone)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => {
          setPendingDelete(null);
          setDeleteError(null);
        }}
        onConfirm={() => void confirmDelete()}
        title={pendingDelete ? `Delete ${pendingDelete.dealName}?` : 'Delete deal?'}
        description={
          pendingLineCount === null
            ? 'Any products on this deal are removed with it. This cannot be undone.'
            : pendingLineCount === 0
              ? 'This deal has no products on it. This cannot be undone.'
              : `This also removes the ${pendingLineCount} ${
                  pendingLineCount === 1 ? 'product' : 'products'
                } on the deal. This cannot be undone.`
        }
        confirmLabel="Delete deal"
        loading={deleting}
        error={deleteError}
      />

      {quotePreview.element}
    </>
  );
}

function DealsEmptyState({
  filtered,
  currencyFiltered,
  projectFiltered,
  columnsHidAll,
  onClearColumns,
  onClear,
  hasError,
  action,
}: {
  filtered: boolean;
  /** A currency code went out on the list request — see the note below. */
  currencyFiltered: boolean;
  /** A project id went out on the list request — see the note below. */
  projectFiltered: boolean;
  /** The server returned deals and the column filters hid every one of them. */
  columnsHidAll: boolean;
  onClearColumns: () => void;
  onClear: () => void;
  hasError: boolean;
  action: ReactNode;
}) {
  if (hasError) {
    return (
      <EmptyState
        icon={<DealIcon />}
        title="Nothing to show"
        description="Couldn’t load the deals. See the message above."
      />
    );
  }

  /*
   * Distinct from "no deals match": the pipeline is not empty and the filter
   * bank is not the cause. The header funnels go with the table, so the way back
   * has to be offered here.
   */
  if (columnsHidAll) {
    return (
      <EmptyState
        icon={<DealIcon />}
        title="Every deal on this page is filtered out"
        description="Column filters hid every deal on this page. Clear them to see it."
        action={
          <Button variant="secondary" size="sm" onClick={onClearColumns}>
            Clear column filters
          </Button>
        }
      />
    );
  }

  /*
   * `currency` is nullable, so a deal recording none matches NO code — an empty
   * currency-filtered list is an ordinary state, not a broken filter. The
   * sentence names that cause without claiming HOW MANY deals lack a currency,
   * so it stays true either way; this component sees only the empty result.
   *
   * The project filter takes precedence because it is the one someone ARRIVES on
   * (the link off `/projects/:id`) rather than sets here, and an empty result is
   * its ordinary answer — a deal is put on a project only by an edit.
   */
  const filteredDescription = projectFiltered
    ? 'No deal names this project.'
    : currencyFiltered
      ? 'Deals with no currency can’t match a currency filter.'
      : 'Try a different name, or clear the filters.';

  return (
    <EmptyState
      icon={<DealIcon />}
      title={filtered ? 'No deals match those filters' : 'No deals yet'}
      description={
        filtered ? filteredDescription : 'Add the first deal.'
      }
      action={
        filtered ? (
          <Button variant="secondary" size="sm" onClick={onClear}>
            Clear filters
          </Button>
        ) : (
          action
        )
      }
    />
  );
}
